<template>
  <div class="overview-two">
    <div class="overview-two__bg"></div>

    <!-- 筛选区：整体椭圆框（对齐总览1.0 control，适配多行条件） -->
    <section class="filter-panel">
      <div class="filter-control">
        <div class="filter-row">
          <span class="filter-label">日期选择</span>
          <el-date-picker
            v-model="dateValue"
            type="daterange"
            align="right"
            unlink-panels
            value-format="yyyy-MM-dd"
            range-separator="-"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :picker-options="pickerOptions"
            :clearable="false"
            class="date-picker"
          />
        </div>

        <!-- 地区范围筛选暂时隐藏，默认全球数据 -->
        <div v-if="false" class="filter-row">
          <span class="filter-label">地区范围</span>
          <div class="pill-group">
            <button
              v-for="item in regionOptions"
              :key="item.value"
              type="button"
              class="pill"
              :class="{ active: region === item.value }"
              @click="selectRegion(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>

        <div class="filter-row">
          <span class="filter-label">设备类型</span>
          <div class="pill-group">
            <button
              type="button"
              class="pill"
              :class="{ active: isAllDevice }"
              @click="selectAllDevice"
            >
              全部设备
            </button>
            <button
              v-for="item in deviceOptions"
              :key="item.value"
              type="button"
              class="pill"
              :class="{ active: !isAllDevice && selectedDevices.includes(item.value) }"
              @click="toggleDevice(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>

        <div class="filter-row filter-row--actions">
          <button
            type="button"
            class="query-btn"
            :disabled="querying"
            @click="handleQuery"
          >
            <i class="el-icon-search"></i>
            查询
          </button>
        </div>
      </div>
    </section>

    <!-- 整体销售概况 -->
    <section
      class="summary-section"
      v-loading="moduleLoading.summary"
      element-loading-background="rgba(0, 0, 0, 0.28)"
      element-loading-text="概况加载中"
    >
      <div class="section-head">
        <h2 class="section-title">整体销售概况</h2>
      </div>
      <div class="metric-grid">
        <div
          v-for="card in metricCards"
          :key="card.key"
          class="metric-card"
          :class="{ active: activeMetric === card.key }"
          @click="selectMetric(card.key)"
        >
          <div class="metric-card__top">
            <div class="metric-icon" :style="{ background: card.iconBg }">
              <i :class="card.icon"></i>
            </div>
            <span class="metric-name">{{ card.name }}</span>
          </div>
          <div class="metric-value">{{ formatValue(card) }}</div>
          <div class="metric-trends">
            <span
              class="trend"
              :class="trendClass(card.yoy)"
            >
              同比
              <template v-if="card.yoy == null"> --</template>
              <template v-else>
                <i :class="card.yoy >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
                {{ Math.abs(card.yoy).toFixed(2) }}%
              </template>
            </span>
            <span
              class="trend"
              :class="trendClass(card.mom)"
            >
              环比
              <template v-if="card.mom == null"> --</template>
              <template v-else>
                <i :class="card.mom >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
                {{ Math.abs(card.mom).toFixed(2) }}%
              </template>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- 指标趋势 -->
    <section class="chart-section">
      <div class="section-head chart-head">
        <div class="chart-head-left">
          <h2 class="section-title">指标筛选</h2>
          <div class="pill-group metric-pills">
            <button
              v-for="card in metricCards"
              :key="'tab-' + card.key"
              type="button"
              class="pill outline"
              :class="{ active: activeMetric === card.key }"
              @click="selectMetric(card.key)"
            >
              {{ card.trendLabel || card.name }}
            </button>
          </div>
        </div>
      </div>
      <div
        class="chart-panel"
        v-loading="moduleLoading.trend || trendLoading"
        element-loading-background="rgba(0, 0, 0, 0.28)"
        element-loading-text="趋势加载中"
      >
        <div class="chart-panel-inner-corners"></div>
        <div class="chart-scanline"></div>
        <div ref="trendChart" class="trend-chart"></div>
      </div>
    </section>

    <!-- 第一行：景区销售额排名 + 设备销售排名 -->
    <section class="bottom-charts">
      <div
        class="bottom-card company-name-card"
        v-loading="moduleLoading.companyName"
        element-loading-background="rgba(0, 0, 0, 0.28)"
        element-loading-text="景区加载中"
      >
        <div class="bottom-card__head">
          <h2 class="section-title">景区销售额排名</h2>
          <span class="section-tip">{{ companyNameTip }}</span>
        </div>
        <div class="company-rank-body">
          <ul v-if="companyNameRankList.length" class="company-rank-list">
            <li
              v-for="item in companyNameRankList"
              :key="item.id || item.name"
              class="company-rank-item"
            >
              <span class="company-rank-no" :class="'rank-' + item.rank">{{ item.rank }}</span>
              <div class="company-rank-main">
                <div class="company-rank-top">
                  <span class="company-rank-name" :title="item.name">
                    {{ item.name }}:
                    <span class="company-rank-money">{{ formatMoney(item.money) }}</span>
                  </span>
                  <span
                    class="rank-change"
                    :class="rankChangeClass(item)"
                    :title="rankChangeTitle(item)"
                  >
                    {{ formatRankChangeText(item, 'company') }}
                  </span>
                </div>
                <div class="company-rank-track">
                  <div class="company-rank-bar" :style="{ width: item.percent + '%' }"></div>
                </div>
              </div>
            </li>
          </ul>
          <div v-else-if="!moduleLoading.companyName" class="chart-empty static">暂无景区销售数据</div>
        </div>
      </div>

      <div
        class="bottom-card company-name-card device-rank-card"
        v-loading="moduleLoading.device"
        element-loading-background="rgba(0, 0, 0, 0.28)"
        element-loading-text="设备排名加载中"
      >
        <div class="bottom-card__head">
          <h2 class="section-title">设备销售排名</h2>
          <span class="section-tip">{{ deviceRankTip }}</span>
        </div>
        <div class="company-rank-body">
          <ul v-if="deviceRankList.length" class="company-rank-list">
            <li
              v-for="item in deviceRankList"
              :key="'device-' + item.rank + '-' + item.name"
              class="company-rank-item"
            >
              <span class="company-rank-no" :class="'rank-' + item.rank">{{ item.rank }}</span>
              <div class="company-rank-main">
                <div class="company-rank-top">
                  <span class="company-rank-name" :title="item.name">
                    {{ item.name }}:
                    <span class="company-rank-money">{{ formatMoney(item.money) }}</span>
                  </span>
                  <span
                    class="rank-change"
                    :class="rankChangeClass(item)"
                    :title="rankChangeTitle(item)"
                  >
                    {{ formatRankChangeText(item, 'device') }}
                  </span>
                </div>
                <div class="company-rank-track">
                  <div class="company-rank-bar" :style="{ width: item.percent + '%' }"></div>
                </div>
              </div>
            </li>
          </ul>
          <div v-else-if="!moduleLoading.device" class="chart-empty static">
            {{ deviceRankTip || '暂无设备销售数据' }}
          </div>
        </div>
      </div>
    </section>

    <!-- 第二行：地区销售额排名 + 设备类型销售额占比（景区类型排名暂时隐藏） -->
    <section class="bottom-charts company-charts">
      <div
        class="bottom-card region-card"
        v-loading="moduleLoading.region || provinceLoading"
        element-loading-background="rgba(0, 0, 0, 0.28)"
        element-loading-text="地区加载中"
      >
        <div class="bottom-card__head">
          <h2 class="section-title">
            <template v-if="regionViewMode === 'province'">省份销售额排名</template>
            <template v-else>
              地区销售额排名
              <span class="section-title-hint">（点击中国大陆可查看省份排名）</span>
            </template>
          </h2>
          <div class="region-head-right">
            <button
              v-if="regionViewMode === 'province'"
              type="button"
              class="region-back-btn"
              @click="backToRegionRank"
            >
              <i class="el-icon-back"></i>
              返回地区
            </button>
            <span class="section-tip">{{ regionPanelTip }}</span>
          </div>
        </div>
        <div class="company-rank-body">
          <ul v-if="regionPanelList.length" class="company-rank-list">
            <li
              v-for="item in regionPanelList"
              :key="(item.code || item.region_id || item.name) + '-' + item.rank"
              class="company-rank-item"
              :class="{ 'is-drillable': item.canDrill }"
              @click="onRegionRankClick(item)"
            >
              <span class="company-rank-no" :class="'rank-' + item.rank">{{ item.rank }}</span>
              <div class="company-rank-main">
                <div class="company-rank-top">
                  <span
                    class="company-rank-name"
                    :class="{ 'is-mainland': item.canDrill }"
                    :title="item.name"
                  >
                    {{ item.name }}:
                    <span class="company-rank-money">{{ formatMoney(item.money) }}</span>
                  </span>
                </div>
                <div class="company-rank-track">
                  <div
                    class="company-rank-bar region-rank-bar"
                    :class="{
                      'is-province': regionViewMode === 'province',
                      'is-mainland-bar': item.canDrill,
                    }"
                    :style="{ width: item.percent + '%' }"
                  ></div>
                </div>
              </div>
            </li>
          </ul>
          <div
            v-else-if="!moduleLoading.region && !provinceLoading"
            class="chart-empty static"
          >
            {{ regionViewMode === 'province' ? '暂无省份销售数据' : '暂无地区销售数据' }}
          </div>
        </div>
      </div>

      <div
        class="bottom-card source-card"
        v-loading="moduleLoading.source"
        element-loading-background="rgba(0, 0, 0, 0.28)"
        element-loading-text="来源加载中"
      >
        <div class="bottom-card__head">
          <h2 class="section-title">设备类型销售额占比</h2>
          <span class="section-tip">{{ sourceTip }}</span>
        </div>
        <div class="source-layout">
          <div class="source-chart-wrap">
            <div ref="sourceChart" class="source-chart"></div>
            <div v-if="!sourcePieData.length && !moduleLoading.source" class="chart-empty">暂无来源销售数据</div>
          </div>
          <div class="source-side-list" v-if="sourceCardList.length">
            <div
              v-for="item in sourceCardList"
              :key="'src-' + item.source"
              class="source-side-item"
            >
              <span class="source-side-dot" :style="{ background: item.color }"></span>
              <span class="source-side-name" :title="item.name">{{ item.name }}</span>
              <span class="source-side-money">{{ formatMoney(item.value) }}</span>
              <span class="source-side-pct">{{ Number(item.percent || 0).toFixed(2) }}%</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 景区类型排名暂时隐藏：恢复时放回第二行左侧即可
    <div class="bottom-card company-type-card" v-if="false">...</div>
    -->
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  getBIOne,
  getNewSaleSum,
  getNewGoodsSum,
  getNewErpCompanySum,
  getNewRefundSum,
  getNewSaleAmountTrend,
  getNewGoodsSumTrend,
  getNewErpCompanyTrend,
  getNewRefundTrend,
  getNewSourceTrend,
  getNewRegionTrend,
  getNewCompanyTypeTrend,
  getNewCompanyProvinceTrend,
  getNewCompanyName,
} from '@/api/bi'

const DEVICE_OPTIONS = [
  { value: '1', label: '771/772' },
  { value: '2', label: '775' },
  { value: '3', label: '776' },
  { value: '4', label: '779' },
  { value: '5', label: '780' },
  { value: '6', label: '781' },
  { value: '7', label: '782' },
  { value: '8', label: '783' },
]

/** 地区字典（先前端写死，region_id → region_name） */
const REGION_NAME_MAP = {
  '1': '中国大陆',
  '2': '中国香港',
  '3': '中国澳门',
  '4': '中国台湾',
  '5': '阿联酋',
  '6': '美国',
  '7': '韩国',
  '8': '加拿大',
  '9': '澳大利亚',
}

/** 省份行政区划码 → 名称（erp_company_province 可能只返回编码） */
const PROVINCE_NAME_MAP = {
  '110000': '北京市',
  '120000': '天津市',
  '130000': '河北省',
  '140000': '山西省',
  '150000': '内蒙古自治区',
  '210000': '辽宁省',
  '220000': '吉林省',
  '230000': '黑龙江省',
  '310000': '上海市',
  '320000': '江苏省',
  '330000': '浙江省',
  '340000': '安徽省',
  '350000': '福建省',
  '360000': '江西省',
  '370000': '山东省',
  '410000': '河南省',
  '420000': '湖北省',
  '430000': '湖南省',
  '440000': '广东省',
  '450000': '广西壮族自治区',
  '460000': '海南省',
  '500000': '重庆市',
  '510000': '四川省',
  '520000': '贵州省',
  '530000': '云南省',
  '540000': '西藏自治区',
  '610000': '陕西省',
  '620000': '甘肃省',
  '630000': '青海省',
  '640000': '宁夏回族自治区',
  '650000': '新疆维吾尔自治区',
  '710000': '台湾省',
  '810000': '香港特别行政区',
  '820000': '澳门特别行政区',
  // 兼容 2 位省码
  '11': '北京市',
  '12': '天津市',
  '13': '河北省',
  '14': '山西省',
  '15': '内蒙古自治区',
  '21': '辽宁省',
  '22': '吉林省',
  '23': '黑龙江省',
  '31': '上海市',
  '32': '江苏省',
  '33': '浙江省',
  '34': '安徽省',
  '35': '福建省',
  '36': '江西省',
  '37': '山东省',
  '41': '河南省',
  '42': '湖北省',
  '43': '湖南省',
  '44': '广东省',
  '45': '广西壮族自治区',
  '46': '海南省',
  '50': '重庆市',
  '51': '四川省',
  '52': '贵州省',
  '53': '云南省',
  '54': '西藏自治区',
  '61': '陕西省',
  '62': '甘肃省',
  '63': '青海省',
  '64': '宁夏回族自治区',
  '65': '新疆维吾尔自治区',
  '71': '台湾省',
  '81': '香港特别行政区',
  '82': '澳门特别行政区',
}

/** 中国大陆省级行政区（31 个，用于省份排行补齐） */
const MAINLAND_PROVINCE_CODES = [
  '110000', '120000', '130000', '140000', '150000',
  '210000', '220000', '230000',
  '310000', '320000', '330000', '340000', '350000', '360000', '370000',
  '410000', '420000', '430000', '440000', '450000', '460000',
  '500000', '510000', '520000', '530000', '540000',
  '610000', '620000', '630000', '640000', '650000',
]

/** 设计稿/看板设备展示名（筛选 value） */
const SOURCE_DISPLAY_NAMES = {
  '1': '771/772',
  '2': '775',
  '3': '776',
  '4': '779',
  '5': '780',
  '6': '781',
  '7': '782',
  '8': '783',
}

/**
 * 总览2.0 库内 source → 展示名
 * Source2/3/5/7 → 771/772；Source6 → 775；其余按表
 * 兼容接口返回 "2" / "2.0" / 2
 */
const SOURCE_DB_LABELS = {
  '2': '771/772',
  '3': '771/772',
  '5': '771/772',
  '6': '775',
  '7': '771/772',
  '9': '776',
  '10': '779-纪念章',
  '11': '780',
  '12': '779-冰箱贴',
  '13': '781',
  '14': '782',
  '15': '783',
}

/**
 * 筛选 value(1~8) → 库内 source 码列表（多码汇总到同一扇区）
 * 1=771/772(2+3+5+7)  2=775(6)  3=776(9)  4=779(10+12)
 * 5=780(11)  6=781(13)  7=782(14)  8=783(15)
 */
const FILTER_TO_DB_SOURCES = {
  '1': ['2', '3', '5', '7'],
  '2': ['6'],
  '3': ['9'],
  '4': ['10', '12'],
  '5': ['11'],
  '6': ['13'],
  '7': ['14'],
  '8': ['15'],
}

const SOURCE_PIE_COLORS = ['#3b82f6', '#22c55e', '#f97316', '#a855f7', '#ef4444', '#06b6d4', '#eab308', '#ec4899']
/** 饼图每类最小可视占比 5% → 对应圆心角 18° */
const SOURCE_PIE_MIN_ANGLE = 18

const METRIC_DEFS = [
  {
    key: 'sales',
    name: '销售额',
    trendLabel: '销售额趋势',
    icon: 'el-icon-s-data',
    iconBg: 'linear-gradient(135deg, #00d4ff, #0284c7)',
    unit: '',
    decimals: 2,
  },
  {
    key: 'qty',
    name: '商品销售数量',
    trendLabel: '销售数量',
    icon: 'el-icon-goods',
    iconBg: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    unit: '',
    decimals: 0,
  },
  {
    key: 'avgPrice',
    name: '平均销售单价',
    trendLabel: '平均销售单价',
    icon: 'el-icon-price-tag',
    iconBg: 'linear-gradient(135deg, #06b6d4, #0e7490)',
    unit: '',
    decimals: 2,
  },
  {
    key: 'refundRate',
    name: '订单退款率',
    trendLabel: '退款率',
    icon: 'el-icon-refresh-left',
    iconBg: 'linear-gradient(135deg, #ec4899, #be185d)',
    unit: '%',
    decimals: 2,
  },
  {
    key: 'storeCount',
    name: '销售景区数量',
    trendLabel: '销售景区数量',
    icon: 'el-icon-office-building',
    iconBg: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    unit: '',
    decimals: 0,
  },
  {
    key: 'deviceCount',
    name: '产生销售设备数量',
    trendLabel: '产生销售设备数量',
    icon: 'el-icon-monitor',
    iconBg: 'linear-gradient(135deg, #22c55e, #15803d)',
    unit: '',
    decimals: 0,
  },
  {
    key: 'skuCount',
    name: '产生销售商品SKU数量',
    trendLabel: '产生销售商品SKU数量',
    icon: 'el-icon-collection-tag',
    iconBg: 'linear-gradient(135deg, #f59e0b, #d97706)',
    unit: '',
    decimals: 0,
  },
  {
    key: 'avgOutput',
    name: '平均单台产值',
    trendLabel: '平均单台产值',
    icon: 'el-icon-s-finance',
    iconBg: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
    unit: '',
    decimals: 2,
  },
]

const EMPTY_SERIES = { current: [], yoy: [], mom: [], xData: [], yoyX: [], momX: [] }

export default {
  name: 'OverviewTotalTwo',
  data() {
    const yesterday = new Date()
    yesterday.setHours(0, 0, 0, 0)
    yesterday.setDate(yesterday.getDate() - 1)
    const start = new Date(yesterday.getFullYear(), 0, 1)
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const getYesterday = () => {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      d.setDate(d.getDate() - 1)
      return d
    }

    return {
      querying: false,
      moduleLoading: {
        summary: false,
        trend: false,
        source: false,
        region: false,
        companyType: false,
        companyName: false,
        device: false,
      },
      dateValue: [fmt(start), fmt(yesterday)],
      pickerOptions: {
        disabledDate(time) {
          // 禁止选择今天及以后
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          return time.getTime() >= today.getTime()
        },
        shortcuts: [
          {
            text: '最近一周',
            onClick(picker) {
              const e = getYesterday()
              const s = new Date(e)
              s.setDate(s.getDate() - 6)
              picker.$emit('pick', [s, e])
            },
          },
          {
            text: '最近一个月',
            onClick(picker) {
              const e = getYesterday()
              const s = new Date(e)
              s.setDate(s.getDate() - 29)
              picker.$emit('pick', [s, e])
            },
          },
          {
            text: '今年至今',
            onClick(picker) {
              const e = getYesterday()
              const s = new Date(e.getFullYear(), 0, 1)
              picker.$emit('pick', [s, e])
            },
          },
        ],
      },
      region: 'global',
      regionOptions: [
        { value: 'global', label: '全球数据' },
        { value: 'domestic', label: '境内数据' },
        { value: 'overseas', label: '境外数据 (包含中国港澳台)' },
      ],
      deviceOptions: DEVICE_OPTIONS,
      selectedDevices: DEVICE_OPTIONS.map((d) => d.value),
      activeMetric: 'sales',
      metricCards: METRIC_DEFS.map((def) => ({
        ...def,
        value: null,
        yoy: null,
        mom: null,
        series: { ...EMPTY_SERIES },
        available: false,
      })),
      metricBundle: null,
      // 趋势按需缓存，查询时只预热销售额趋势
      trendCache: {
        sale: null,
        goods: null,
        erp: null,
        refund: null,
      },
      trendLoading: false,
      chart: null,
      sourceChart: null,
      resizeHandler: null,
      regionViewMode: 'region', // region | province
      provinceList: [],
      provinceLoading: false,
      dataTip: '新版销售分析接口 · 点击卡片切换趋势',
      chartTip: '加载趋势中…',
      sourceTip: '按设备来源汇总销售额',
      companyTypeTip: '按景区类型汇总销售额',
      companyNameTip: '全部景区销售额排名',
      deviceRankTip: '设备销售额排名',
      sourceList: [],
      regionList: [],
      regionNameMap: { ...REGION_NAME_MAP },
      companyTypeList: [],
      companyTypeMomRankMap: {},
      companyNameList: [],
      companyNameMomRankMap: {},
      deviceRankRaw: null,
      deviceMomRankMap: {},
    }
  },
  computed: {
    isAllDevice() {
      return this.selectedDevices.length === this.deviceOptions.length
    },
    // 设备销售排名列表（与景区销售额排名同结构）
    deviceRankData() {
      const raw = this.deviceRankRaw
      if (!raw || !Array.isArray(raw.xData) || !Array.isArray(raw.yData) || !raw.yData.length) {
        return null
      }
      return {
        xData: raw.xData.slice(),
        yData: raw.yData.map((name) => (name != null ? String(name) : '')),
      }
    },
    // 兼容旧模板/热更新残留引用，避免 deviceRankList.length 报错
    deviceRankList() {
      const data = this.deviceRankData
      if (!data) return []
      const rows = data.yData.map((name, index) => ({
        name: name || `设备${index + 1}`,
        money: Number(data.xData[index]) || 0,
      }))
      const sorted = rows.sort((a, b) => b.money - a.money)
      const maxMoney = sorted.length ? sorted[0].money : 0
      const momMap = this.deviceMomRankMap || {}
      return sorted.map((item, index) => {
        const rank = index + 1
        const prevRank = momMap[item.name] != null ? momMap[item.name] : null
        const rankDelta = prevRank != null ? prevRank - rank : null
        return {
          ...item,
          rank,
          prevRank,
          rankDelta,
          percent: maxMoney > 0 ? Math.max(4, (item.money / maxMoney) * 100) : 0,
        }
      })
    },
    sourcePieData() {
      const bySource = {}
      if (Array.isArray(this.sourceList)) {
        this.sourceList.forEach((item) => {
          const source = this.normalizeSourceCode(item.source)
          if (!source) return
          if (!bySource[source]) {
            bySource[source] = { source, sale_money: 0, sale_num: 0 }
          }
          bySource[source].sale_money += Number(item.sale_money) || 0
          bySource[source].sale_num += Number(item.sale_num) || 0
        })
      }

      // 按当前筛选设备类型汇总对应库内 source（缺失为 0），保证每类都有扇区
      const filterKeys = (this.selectedDevices && this.selectedDevices.length)
        ? this.selectedDevices
        : DEVICE_OPTIONS.map((d) => d.value)
      const list = filterKeys.map((filterVal) => {
        const codes = FILTER_TO_DB_SOURCES[String(filterVal)] || [String(filterVal)]
        let sale_money = 0
        let sale_num = 0
        codes.forEach((code) => {
          const row = bySource[code]
          if (!row) return
          sale_money += Number(row.sale_money) || 0
          sale_num += Number(row.sale_num) || 0
        })
        return {
          source: String(filterVal),
          db_sources: codes,
          sale_money,
          sale_num,
        }
      })
      if (!list.length) return []

      const total = list.reduce((sum, item) => sum + (Number(item.sale_money) || 0), 0)
      return list
        .map((item) => {
          const money = Number(item.sale_money) || 0
          return {
            name: this.getFilterDeviceLabel(item.source),
            value: money,
            sale_money: money,
            source: item.source,
            sale_num: Number(item.sale_num) || 0,
            percent: total > 0 ? (money / total) * 100 : 0,
          }
        })
        .sort((a, b) => b.value - a.value)
        .map((item, index) => ({
          ...item,
          color: SOURCE_PIE_COLORS[index % SOURCE_PIE_COLORS.length],
        }))
    },
    sourceCardList() {
      return this.sourcePieData
    },
    // 饼图：不合并，保留每一个设备类型（按销售额降序）
    sourceChartSlices() {
      return (this.sourcePieData || []).map((item) => ({
        name: item.name,
        value: Number(item.value) || 0,
        sale_num: item.sale_num,
        percent: item.percent,
        color: item.color,
        source: item.source,
      }))
    },
    regionRankList() {
      if (!Array.isArray(this.regionList) || !this.regionList.length) return []
      const sorted = [...this.regionList]
        .map((item) => ({
          name: this.resolveRegionName(item),
          money: Number(item.sale_money) || 0,
          num: Number(item.sale_num) || 0,
          region_id: item.region_id,
        }))
        .sort((a, b) => b.money - a.money)
      const maxMoney = sorted.length ? sorted[0].money : 0
      return sorted.map((item, index) => {
        const row = {
          ...item,
          rank: index + 1,
          percent: maxMoney > 0 ? Math.max(4, (item.money / maxMoney) * 100) : 0,
        }
        return {
          ...row,
          canDrill: this.isMainlandChinaRegion(row),
        }
      })
    },
    provinceRankList() {
      const moneyByCode = {}
      const numByCode = {}

      ;(Array.isArray(this.provinceList) ? this.provinceList : []).forEach((item) => {
        const code = this.resolveProvinceCode(item)
        if (!code || !MAINLAND_PROVINCE_CODES.includes(code)) return
        moneyByCode[code] = (moneyByCode[code] || 0) + (Number(item && item.sale_money) || 0)
        numByCode[code] = (numByCode[code] || 0) + (Number(item && item.sale_num) || 0)
      })

      // 固定 31 省：后端未返回的省份金额/销量默认为 0，排名从 1 开始
      const sorted = MAINLAND_PROVINCE_CODES
        .map((code) => ({
          name: PROVINCE_NAME_MAP[code] || `省份${code}`,
          code,
          money: moneyByCode[code] || 0,
          num: numByCode[code] || 0,
        }))
        .sort((a, b) => {
          if (b.money !== a.money) return b.money - a.money
          return String(a.name).localeCompare(String(b.name), 'zh-CN')
        })
      const maxMoney = sorted.length ? sorted[0].money : 0
      return sorted.map((item, index) => ({
        ...item,
        rank: index + 1,
        percent: maxMoney > 0 ? Math.max(4, (item.money / maxMoney) * 100) : 0,
        canDrill: false,
      }))
    },
    regionPanelList() {
      return this.regionViewMode === 'province' ? this.provinceRankList : this.regionRankList
    },
    regionRankTip() {
      if (this.region === 'domestic') return '境内 · 省份维度'
      if (this.region === 'overseas') return '境外 · 地区维度'
      return '全球 · 地区维度'
    },
    regionPanelTip() {
      if (this.regionViewMode === 'province') {
        return this.provinceRankList.length
          ? `中国大陆 · 共 ${this.provinceRankList.length} 省`
          : '中国大陆 · 省份销售'
      }
      const base = this.regionRankTip
      return this.regionRankList.some((item) => item.canDrill)
        ? `${base} · 点击中国大陆查看省份`
        : base
    },
    companyTypeRankList() {
      if (!Array.isArray(this.companyTypeList) || !this.companyTypeList.length) return []
      const sorted = [...this.companyTypeList]
        .map((item) => ({
          name: item.erp_company_type || '未知类型',
          money: Number(item.sale_money) || 0,
          num: Number(item.sale_num) || 0,
        }))
        .sort((a, b) => b.money - a.money)
      const maxMoney = sorted.length ? sorted[0].money : 0
      const momMap = this.companyTypeMomRankMap || {}
      return sorted.map((item, index) => {
        const rank = index + 1
        const prevRank = momMap[item.name] != null ? momMap[item.name] : null
        const rankDelta = prevRank != null ? prevRank - rank : null
        return {
          ...item,
          rank,
          prevRank,
          rankDelta,
          percent: maxMoney > 0 ? Math.max(4, (item.money / maxMoney) * 100) : 0,
        }
      })
    },
    companyNameRankList() {
      if (!Array.isArray(this.companyNameList) || !this.companyNameList.length) return []
      const sorted = [...this.companyNameList]
        .map((item) => ({
          id: item.erp_company_id || '',
          name: item.erp_company_name || '未知景区',
          money: Number(item.sale_money) || 0,
          num: Number(item.sale_num) || 0,
        }))
        .sort((a, b) => b.money - a.money)
      const maxMoney = sorted.length ? sorted[0].money : 0
      const momMap = this.companyNameMomRankMap || {}
      return sorted.map((item, index) => {
        const rank = index + 1
        const prevRank = (item.id && momMap[`id:${item.id}`] != null)
          ? momMap[`id:${item.id}`]
          : (momMap[`name:${item.name}`] != null ? momMap[`name:${item.name}`] : null)
        const rankDelta = prevRank != null ? prevRank - rank : null
        return {
          ...item,
          rank,
          prevRank,
          rankDelta,
          percent: maxMoney > 0 ? Math.max(4, (item.money / maxMoney) * 100) : 0,
        }
      })
    },
  },
  mounted() {
    this.handleQuery()
    this.$nextTick(() => {
      this.initChart()
      this.initSourceChart()
    })
    this.resizeHandler = () => {
      if (this.chart) this.chart.resize()
      if (this.sourceChart) this.sourceChart.resize()
    }
    window.addEventListener('resize', this.resizeHandler)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    if (this.sourceChart) {
      this.sourceChart.dispose()
      this.sourceChart = null
    }
  },
  methods: {
    trendClass(val) {
      if (val == null) return 'na'
      return val >= 0 ? 'up' : 'down'
    },
    selectRegion(value) {
      this.region = value
    },
    selectAllDevice() {
      this.selectedDevices = this.deviceOptions.map((d) => d.value)
    },
    toggleDevice(value) {
      // 全选状态下点某一个：只选中该项（而不是从全选里去掉它）
      if (this.isAllDevice) {
        this.selectedDevices = [value]
        return
      }
      // 非全选：支持多选开关
      const set = new Set(this.selectedDevices)
      if (set.has(value)) {
        if (set.size === 1) return
        set.delete(value)
      } else {
        set.add(value)
      }
      this.selectedDevices = Array.from(set)
    },
    // 全部=0，境内=1，境外=-1
    getRegionId() {
      if (this.region === 'domestic') return 1
      if (this.region === 'overseas') return -1
      return 0
    },
    buildQueryParams() {
      const start = this.dateValue && this.dateValue[0]
      const end = this.dateValue && this.dateValue[1]
      return {
        start,
        end,
        source: [...this.selectedDevices],
        region_id: this.getRegionId(),
      }
    },
    parseYmd(str) {
      if (!str) return null
      const parts = String(str).split('-').map(Number)
      if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null
      return new Date(parts[0], parts[1] - 1, parts[2])
    },
    formatYmd(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    // 等长上一周期：环比区间
    getPreviousPeriodRange(start, end) {
      const startDate = this.parseYmd(start)
      const endDate = this.parseYmd(end)
      if (!startDate || !endDate || endDate < startDate) return null
      const dayMs = 24 * 60 * 60 * 1000
      const days = Math.round((endDate - startDate) / dayMs) + 1
      const prevEnd = new Date(startDate.getTime() - dayMs)
      const prevStart = new Date(prevEnd.getTime() - (days - 1) * dayMs)
      return {
        start: this.formatYmd(prevStart),
        end: this.formatYmd(prevEnd),
      }
    },
    buildMomQueryParams(baseParams) {
      if (!baseParams || !baseParams.start || !baseParams.end) return null
      const range = this.getPreviousPeriodRange(baseParams.start, baseParams.end)
      if (!range) return null
      return {
        ...baseParams,
        source: Array.isArray(baseParams.source) ? [...baseParams.source] : baseParams.source,
        start: range.start,
        end: range.end,
      }
    },
    rankChangeClass(item) {
      if (!item || item.prevRank == null) return 'new'
      if (item.rankDelta > 0) return 'up'
      if (item.rankDelta < 0) return 'down'
      return 'flat'
    },
    formatRankChangeText(item, type = 'company') {
      if (!item || item.prevRank == null) {
        if (type === 'device') return '新设备'
        if (type === 'type') return '新类型'
        return '新景区'
      }
      if (item.rankDelta === 0) return '环比持平'
      if (item.rankDelta > 0) return `环比⬆️${item.rankDelta}名`
      return `环比⬇️${Math.abs(item.rankDelta)}名`
    },
    rankChangeTitle(item) {
      if (!item) return ''
      if (item.prevRank == null) return '环比区间无此记录（新进榜）'
      if (item.rankDelta === 0) return `环比持平（第 ${item.prevRank} 名）`
      if (item.rankDelta > 0) return `环比上升 ${item.rankDelta} 名（上期第 ${item.prevRank} 名）`
      return `环比下降 ${Math.abs(item.rankDelta)} 名（上期第 ${item.prevRank} 名）`
    },
    buildCompanyTypeMomRankMap(list) {
      const sorted = (Array.isArray(list) ? list : [])
        .map((item) => this.parseCompanyTypeRow(item))
        .filter((item) => item && (item.erp_company_type || item.sale_money > 0))
        .sort((a, b) => (Number(b.sale_money) || 0) - (Number(a.sale_money) || 0))
      const map = {}
      sorted.forEach((item, index) => {
        const name = item.erp_company_type || '未知类型'
        map[name] = index + 1
      })
      return map
    },
    buildCompanyMomRankMap(list) {
      const sorted = (Array.isArray(list) ? list : [])
        .map((item) => this.parseCompanyNameRow(item))
        .filter((item) => item && (item.erp_company_name || item.sale_money > 0))
        .sort((a, b) => (Number(b.sale_money) || 0) - (Number(a.sale_money) || 0))
      const map = {}
      sorted.forEach((item, index) => {
        const rank = index + 1
        if (item.erp_company_id) map[`id:${item.erp_company_id}`] = rank
        if (item.erp_company_name) map[`name:${item.erp_company_name}`] = rank
      })
      return map
    },
    buildDeviceMomRankMap(data) {
      const sort = data && data.device_sort
      if (!sort || !Array.isArray(sort.xData) || !Array.isArray(sort.yData)) return {}
      const rows = sort.yData.map((name, index) => ({
        name: name != null ? String(name) : `设备${index + 1}`,
        money: Number(sort.xData[index]) || 0,
      }))
      rows.sort((a, b) => b.money - a.money)
      const map = {}
      rows.forEach((item, index) => {
        map[item.name] = index + 1
      })
      return map
    },
    formatValue(card) {
      if (card.value == null || Number.isNaN(Number(card.value))) return '--'
      const num = Number(card.value)
      const fixed = num.toFixed(card.decimals)
      if (card.unit === '%') return `${fixed}%`
      const [intPart, decimalPart] = fixed.split('.')
      const withComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return decimalPart != null ? `${withComma}.${decimalPart}` : withComma
    },
    toNum(val) {
      if (val == null || val === '') return null
      const n = Number(val)
      return Number.isNaN(n) ? null : n
    },
    // 环比/同比：(current − previous) / previous × 100；分母为 0 返回 null
    calcChangeRate(current, previous) {
      const cur = this.toNum(current)
      const prev = this.toNum(previous)
      if (cur == null || prev == null) return null
      if (prev === 0) return null
      return Number((((cur - prev) / Math.abs(prev)) * 100).toFixed(2))
    },
    pickRate(...candidates) {
      for (let i = 0; i < candidates.length; i++) {
        const n = this.toNum(candidates[i])
        if (n != null) return Number(n.toFixed(2))
      }
      return null
    },
    safeDiv(a, b, decimals = 2) {
      const num = this.toNum(a)
      const den = this.toNum(b)
      if (num == null || den == null || den === 0) return null
      return Number((num / den).toFixed(decimals))
    },
    calcRefundRate(refundNum, saleNum) {
      const refund = this.toNum(refundNum) || 0
      const sale = this.toNum(saleNum) || 0
      if (!refund && !sale) return 0
      return Number(((refund / (sale + refund)) * 100).toFixed(2))
    },
    unwrapSettled(res) {
      if (!res) return null
      // 拦截器已通过：直接取 data
      if (res.status === 'fulfilled' && res.value) {
        const v = res.value
        if (v.data !== undefined && v.data !== null) return v.data
        // 兼容直接返回业务体
        if (v.current !== undefined || v.money !== undefined || v.num !== undefined) return v
        return null
      }
      // Network 有数据但业务 code 被拦截器 reject 时，仍尝试取 data，避免页面空白
      if (res.status === 'rejected' && res.reason) {
        const r = res.reason
        // 兼容 axios HTTP 错误：reason.response.data
        const payload = (r && r.data !== undefined) ? r : (r && r.response && r.response.data) || null
        const data = payload && payload.data
        if (data != null && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length) {
          return data
        }
        if (Array.isArray(data) && data.length) return data
      }
      return null
    },
    // 从 allSettled 结果提取失败文案（单接口失败不阻断整页）
    getSettledError(res) {
      if (!res || res.status !== 'rejected' || !res.reason) return ''
      const r = res.reason
      const payload = (r && r.msg !== undefined) ? r : (r.response && r.response.data) || r
      return (payload && (payload.msg || payload.message)) || ''
    },
    collectPartialErrors(namedResults) {
      const msgs = []
      namedResults.forEach(({ name, res }) => {
        const msg = this.getSettledError(res)
        if (msg) msgs.push(`${name}：${msg}`)
        else if (res && res.status === 'rejected') msgs.push(`${name}：请求失败`)
      })
      return msgs
    },
    // 兼容 current 为对象 / 数字 / [数字] / [{...}] / 扁平结构
    getPeriod(container, key) {
      if (!container || typeof container !== 'object') return {}
      let period = container[key]
      if (period == null && key === 'current') {
        const flatKeys = ['money', 'num', 'device_num', 'sum_money', 'sum_num', 'goods_num', 'erp_company_num', 'refund_num']
        if (flatKeys.some((k) => container[k] != null)) period = container
      }
      // 纯数字：{ current: 569 }
      if (typeof period === 'number' || (typeof period === 'string' && period !== '' && !Number.isNaN(Number(period)))) {
        return { _scalar: period }
      }
      if (Array.isArray(period)) {
        if (!period.length) return {}
        const first = period[0]
        // [569] 这种简化结构
        if (typeof first === 'number' || (typeof first === 'string' && first !== '' && !Number.isNaN(Number(first)))) {
          return { _scalar: first }
        }
        return first && typeof first === 'object' ? first : {}
      }
      return period && typeof period === 'object' ? period : {}
    },
    pickField(obj, keys) {
      if (!obj) return null
      // 兼容 current: [569] / current: 569
      if (obj._scalar != null && obj._scalar !== '') return obj._scalar
      for (let i = 0; i < keys.length; i++) {
        if (obj[keys[i]] != null && obj[keys[i]] !== '') return obj[keys[i]]
      }
      return null
    },
    // 从汇总接口组装 8 张卡片指标（含同比环比）
    buildMetricBundle(saleSum, goodsSum, erpSum, refundSum) {
      const curSale = this.getPeriod(saleSum, 'current')
      const prevSale = this.getPeriod(saleSum, 'previous')
      const yoySale = this.getPeriod(saleSum, 'last_year')

      const moneyKeys = ['money', 'sum_money', 'total_money', 'sale_money', 'amount']
      const numKeys = ['num', 'sum_num', 'total_num', 'sale_num', 'order_num']
      const deviceKeys = ['device_num', 'sold_device_num', 'device_count']

      const money = saleSum ? (this.toNum(this.pickField(curSale, moneyKeys)) || 0) : null
      const num = saleSum ? (this.toNum(this.pickField(curSale, numKeys)) || 0) : null
      const deviceNum = saleSum ? (this.toNum(this.pickField(curSale, deviceKeys)) || 0) : null

      const goodsCurObj = this.getPeriod(goodsSum, 'current')
      const goodsPrevObj = this.getPeriod(goodsSum, 'previous')
      const goodsYoyObj = this.getPeriod(goodsSum, 'last_year')
      const goodsCur = goodsSum
        ? (this.toNum(this.pickField(goodsCurObj, ['goods_num', 'sku_num', 'num'])) || 0)
        : null
      const goodsPrev = this.toNum(this.pickField(goodsPrevObj, ['goods_num', 'sku_num', 'num']))
      const goodsYoy = this.toNum(this.pickField(goodsYoyObj, ['goods_num', 'sku_num', 'num']))

      const erpCurObj = this.getPeriod(erpSum, 'current')
      const erpPrevObj = this.getPeriod(erpSum, 'previous')
      const erpYoyObj = this.getPeriod(erpSum, 'last_year')
      const erpCur = erpSum
        ? (this.toNum(this.pickField(erpCurObj, ['erp_company_num', 'company_num', 'store_num'])) || 0)
        : null
      const erpPrev = this.toNum(this.pickField(erpPrevObj, ['erp_company_num', 'company_num', 'store_num']))
      const erpYoy = this.toNum(this.pickField(erpYoyObj, ['erp_company_num', 'company_num', 'store_num']))

      const refundCurObj = this.getPeriod(refundSum, 'current')
      const refundPrevObj = this.getPeriod(refundSum, 'previous')
      const refundYoyObj = this.getPeriod(refundSum, 'last_year')
      const refundCur = refundSum
        ? (this.toNum(this.pickField(refundCurObj, ['refund_num', 'num', 'sum_num'])) || 0)
        : null
      const refundPrev = this.toNum(this.pickField(refundPrevObj, ['refund_num', 'num', 'sum_num'])) || 0
      const refundYoy = this.toNum(this.pickField(refundYoyObj, ['refund_num', 'num', 'sum_num'])) || 0

      const prevMoney = this.toNum(this.pickField(prevSale, moneyKeys))
      const prevNum = this.toNum(this.pickField(prevSale, numKeys))
      const prevDevice = this.toNum(this.pickField(prevSale, deviceKeys))
      const yoyMoney = this.toNum(this.pickField(yoySale, moneyKeys))
      const yoyNum = this.toNum(this.pickField(yoySale, numKeys))
      const yoyDevice = this.toNum(this.pickField(yoySale, deviceKeys))

      const avgPrice = this.safeDiv(money, num, 2)
      const avgOutput = this.safeDiv(money, deviceNum, 2)
      const refundRate = (saleSum && refundSum)
        ? this.calcRefundRate(refundCur, num)
        : null

      return {
        sales: {
          value: money,
          yoy: this.pickRate(saleSum && saleSum.money_yoy, this.calcChangeRate(money, yoyMoney)),
          mom: this.pickRate(saleSum && saleSum.money_chain, this.calcChangeRate(money, prevMoney)),
        },
        qty: {
          value: num,
          yoy: this.pickRate(saleSum && saleSum.num_yoy, this.calcChangeRate(num, yoyNum)),
          mom: this.pickRate(saleSum && saleSum.num_chain, this.calcChangeRate(num, prevNum)),
        },
        avgPrice: {
          value: avgPrice,
          yoy: this.calcChangeRate(avgPrice, this.safeDiv(yoyMoney, yoyNum, 2)),
          mom: this.calcChangeRate(avgPrice, this.safeDiv(prevMoney, prevNum, 2)),
        },
        refundRate: {
          value: refundRate,
          yoy: this.calcChangeRate(refundRate, this.calcRefundRate(refundYoy, yoyNum)),
          mom: this.calcChangeRate(refundRate, this.calcRefundRate(refundPrev, prevNum)),
        },
        storeCount: {
          value: erpCur,
          yoy: this.calcChangeRate(erpCur, erpYoy),
          mom: this.calcChangeRate(erpCur, erpPrev),
        },
        deviceCount: {
          value: deviceNum,
          yoy: this.pickRate(saleSum && saleSum.device_num_yoy, this.calcChangeRate(deviceNum, yoyDevice)),
          mom: this.pickRate(saleSum && saleSum.device_num_chain, this.calcChangeRate(deviceNum, prevDevice)),
        },
        skuCount: {
          value: goodsCur,
          yoy: this.calcChangeRate(goodsCur, goodsYoy),
          mom: this.calcChangeRate(goodsCur, goodsPrev),
        },
        avgOutput: {
          value: avgOutput,
          yoy: this.calcChangeRate(avgOutput, this.safeDiv(yoyMoney, yoyDevice, 2)),
          mom: this.calcChangeRate(avgOutput, this.safeDiv(prevMoney, prevDevice, 2)),
        },
      }
    },
    // 趋势接口固定三字段：current（本期）/ previous（环比）/ last_year（同比）
    getTrendPeriodLists(trendData) {
      if (!trendData || typeof trendData !== 'object') {
        return { current: [], previous: [], last_year: [] }
      }
      const toList = (val) => {
        if (Array.isArray(val)) return val.slice()
        // 单日对象
        if (val && typeof val === 'object') {
          if (
            val.sale_add_date != null
            || val.money != null
            || val.num != null
            || val.device_num != null
            || val.date != null
          ) {
            return [val]
          }
        }
        return []
      }
      return {
        current: toList(trendData.current),
        previous: toList(trendData.previous),
        last_year: toList(trendData.last_year),
      }
    },
    // 取日趋势某字段；兼容：
    // 1) [["2026-01-01", 384], ...] 日期-值对
    // 2) [{sale_add_date, num}, ...] 对象数组
    // 3) [569, 570] 纯数字数组
    extractTrendField(trendData, fieldKeys) {
      const keys = Array.isArray(fieldKeys) ? fieldKeys : [fieldKeys]
      const periods = this.getTrendPeriodLists(trendData)
      const current = periods.current
      const previous = periods.previous
      const lastYear = periods.last_year

      const readPoint = (row, i, valueIndex = 1) => {
        if (row == null || row === '') {
          return { date: String(i + 1), value: 0 }
        }
        // ["2026-01-01", money, num?, device_num?]
        if (Array.isArray(row)) {
          if (row.length >= 2) {
            return {
              date: row[0] != null ? String(row[0]) : String(i + 1),
              value: this.toNum(row[valueIndex] != null ? row[valueIndex] : row[1]) || 0,
            }
          }
          return { date: String(i + 1), value: this.toNum(row[0]) || 0 }
        }
        if (typeof row === 'number' || typeof row === 'string') {
          return { date: String(i + 1), value: this.toNum(row) || 0 }
        }
        if (typeof row === 'object') {
          return {
            date: row.sale_add_date || row.date || row.name || String(i + 1),
            value: this.toNum(this.pickField(row, keys)) || 0,
          }
        }
        return { date: String(i + 1), value: 0 }
      }

      const xData = current.map((row, i) => readPoint(row, i).date)
      const pick = (arr, valueIndex) => arr.map((row, i) => readPoint(row, i, valueIndex).value)
      const pickDates = (arr) => arr.map((row, i) => readPoint(row, i).date)

      return {
        xData,
        current: pick(current, 1),
        mom: pick(previous, 1),
        yoy: pick(lastYear, 1),
        momX: pickDates(previous),
        yoyX: pickDates(lastYear),
      }
    },
    emptySeries() {
      return { current: [], yoy: [], mom: [], xData: [], yoyX: [], momX: [] }
    },
    // 解析单日销售趋势行 → { date, money, num, device }
    // tupleOrder: 'money-first' = [date, money, num, device]；'num-first' = [date, num, money, device]
    parseSaleTrendRow(row, index, tupleOrder = 'money-first') {
      const moneyKeys = ['money', 'sum_money', 'total_money', 'sale_money', 'amount', 'sale_amount']
      const numKeys = ['num', 'sum_num', 'total_num', 'sale_num', 'order_num']
      const deviceKeys = ['device_num', 'sold_device_num', 'device_count', 'deviceNum', 'soldDeviceNum']
      if (row == null || row === '') {
        return { date: String(index + 1), money: 0, num: 0, device: 0 }
      }
      if (Array.isArray(row)) {
        const date = row[0] != null ? String(row[0]) : String(index + 1)
        if (tupleOrder === 'num-first') {
          return {
            date,
            num: this.toNum(row[1]) || 0,
            money: this.toNum(row[2]) || 0,
            device: this.toNum(row[3]) || 0,
          }
        }
        return {
          date,
          money: this.toNum(row[1]) || 0,
          num: this.toNum(row[2]) || 0,
          device: this.toNum(row[3]) || 0,
        }
      }
      if (typeof row === 'number' || typeof row === 'string') {
        return { date: String(index + 1), money: this.toNum(row) || 0, num: 0, device: 0 }
      }
      if (typeof row === 'object') {
        return {
          date: String(row.sale_add_date || row.date || row.name || (index + 1)),
          money: this.toNum(this.pickField(row, moneyKeys)) || 0,
          num: this.toNum(this.pickField(row, numKeys)) || 0,
          device: this.toNum(this.pickField(row, deviceKeys)) || 0,
        }
      }
      return { date: String(index + 1), money: 0, num: 0, device: 0 }
    },
    // 用汇总卡片的销售额，判断元组第二列是金额还是数量
    detectSaleTupleOrder(saleTrend) {
      const sample = Array.isArray(saleTrend && saleTrend.current) ? saleTrend.current[0] : null
      if (!Array.isArray(sample) || sample.length < 3) return 'money-first'
      const bundleMoney = this.metricBundle && this.metricBundle.sales
        ? this.toNum(this.metricBundle.sales.value)
        : null
      if (bundleMoney == null) return 'money-first'
      const asMoneyFirst = Math.abs((this.toNum(sample[1]) || 0) - bundleMoney)
      const asNumFirst = Math.abs((this.toNum(sample[2]) || 0) - bundleMoney)
      return asNumFirst < asMoneyFirst ? 'num-first' : 'money-first'
    },
    // 若解析后「数量序列」更像销售额，说明 money/num 对调了，纠正回来
    ensureMoneyNumOrder(money, qty, device) {
      const bundleMoney = this.metricBundle && this.metricBundle.sales
        ? this.toNum(this.metricBundle.sales.value)
        : null
      if (bundleMoney == null || !money || !qty) return { money, qty, device }
      const sumArr = (arr) => (Array.isArray(arr) ? arr : []).reduce((s, v) => s + (this.toNum(v) || 0), 0)
      const moneySum = sumArr(money.current)
      const qtySum = sumArr(qty.current)
      if (!moneySum && !qtySum) return { money, qty, device }
      const moneyDiff = Math.abs(moneySum - bundleMoney)
      const qtyDiff = Math.abs(qtySum - bundleMoney)
      // 数量序列的合计更接近销售额卡片 → 字段反了，交换
      if (qtyDiff < moneyDiff) {
        console.warn('[总览2.0] 检测到销售额/销售数量字段顺序反了，已自动对调')
        return { money: qty, qty: money, device }
      }
      return { money, qty, device }
    },
    // 从销售金额趋势中拆出 money / num / device_num（供前端合成单价、单台产值）
    parseSaleTrendBundle(saleTrend) {
      const empty = this.emptySeries()
      if (!saleTrend) return { money: empty, qty: empty, device: empty }

      const moneyKeys = ['money', 'sum_money', 'total_money', 'sale_money', 'amount', 'sale_amount']
      const numKeys = ['num', 'sum_num', 'total_num', 'sale_num', 'order_num']
      const deviceKeys = ['device_num', 'sold_device_num', 'device_count', 'deviceNum', 'soldDeviceNum']

      const periods = this.getTrendPeriodLists(saleTrend)
      const sample = periods.current[0]
      const looksLikeDayRows = !!(sample && (
        (typeof sample === 'object' && !Array.isArray(sample) && (
          sample.sale_add_date != null
          || sample.money != null
          || sample.num != null
          || sample.device_num != null
          || sample.date != null
        ))
        || (Array.isArray(sample) && sample.length >= 2)
      ))

      // 优先走文档标准结构：{ current, previous, last_year } 日明细
      // 避免被顶层多余的 money/num 字段误导进“分字段”分支，导致环比/同比丢失或错位
      if (looksLikeDayRows) {
        const tupleOrder = this.detectSaleTupleOrder({ current: periods.current })
        const buildFromRows = (arr) => {
          const list = Array.isArray(arr) ? arr : []
          return list.map((row, i) => this.parseSaleTrendRow(row, i, tupleOrder))
        }
        const currentRows = buildFromRows(periods.current)
        const previousRows = buildFromRows(periods.previous)
        const lastYearRows = buildFromRows(periods.last_year)
        if (!currentRows.length) {
          return { money: empty, qty: empty, device: empty }
        }

        // 单日查询时：趋势若缺字段，用汇总卡片值补齐
        if (currentRows.length === 1 && this.metricBundle) {
          const row = currentRows[0]
          if (!row.money && this.metricBundle.sales) {
            row.money = this.toNum(this.metricBundle.sales.value) || 0
          }
          if (!row.num && this.metricBundle.qty) {
            row.num = this.toNum(this.metricBundle.qty.value) || 0
          }
          if (!row.device && this.metricBundle.deviceCount) {
            row.device = this.toNum(this.metricBundle.deviceCount.value) || 0
          }
        }

        if (currentRows.every((r) => !r.device)) {
          console.warn(
            '[总览2.0] 销售趋势缺少 device_num，单台产值=销售额/设备数 无法计算。样本=',
            periods.current[0],
          )
        }

        const toSeries = (field) => ({
          xData: currentRows.map((r) => r.date),
          current: currentRows.map((r) => r[field]),
          mom: previousRows.map((r) => r[field]),
          yoy: lastYearRows.map((r) => r[field]),
          momX: previousRows.map((r) => r.date),
          yoyX: lastYearRows.map((r) => r.date),
        })

        const ordered = this.ensureMoneyNumOrder(
          toSeries('money'),
          toSeries('num'),
          toSeries('device'),
        )

        // 同比环比完全一致时提示（便于排查接口）
        const sameMomYoy = ordered.money.mom.length
          && ordered.money.mom.length === ordered.money.yoy.length
          && ordered.money.mom.every((v, i) => v === ordered.money.yoy[i])
        if (sameMomYoy) {
          console.warn(
            '[总览2.0] 销售额环比与同比序列值完全相同，请核对接口 previous / last_year',
            {
              previousLen: periods.previous.length,
              lastYearLen: periods.last_year.length,
              previousSample: periods.previous[0],
              lastYearSample: periods.last_year[0],
            },
          )
        }

        return ordered
      }

      // 分字段返回：{ money:{current:[]}, num:{...}, device_num:{...} }
      const isSplitBlock = (block) => !!(
        block
        && typeof block === 'object'
        && (Array.isArray(block.current) || Array.isArray(block.previous) || Array.isArray(block.last_year))
      )
      if (isSplitBlock(saleTrend.money) || isSplitBlock(saleTrend.num) || isSplitBlock(saleTrend.device_num)) {
        const asTrend = (block, keys) => {
          if (!block) return this.emptySeries()
          if (Array.isArray(block)) {
            // 仅有数组时，补上外层的环比/同比日列
            return this.extractTrendField({
              current: block,
              previous: periods.previous,
              last_year: periods.last_year,
            }, keys)
          }
          return this.extractTrendField(block, keys)
        }
        return this.ensureMoneyNumOrder(
          asTrend(saleTrend.money, moneyKeys),
          asTrend(saleTrend.num, numKeys),
          asTrend(saleTrend.device_num, deviceKeys),
        )
      }

      return { money: empty, qty: empty, device: empty }
    },
    // 前端合成比率趋势：分子 / 分母（按索引对齐）
    composeRatioSeries(numerator, denominator, decimals = 2) {
      if (!numerator || !numerator.xData || !numerator.xData.length) return this.emptySeries()
      const den = denominator || this.emptySeries()
      const mapPeriod = (period) => {
        const numArr = (numerator[period] && Array.isArray(numerator[period])) ? numerator[period] : []
        const denArr = (den[period] && Array.isArray(den[period])) ? den[period] : []
        const len = numerator.xData.length
        const out = []
        for (let i = 0; i < len; i++) {
          const a = this.toNum(numArr[i]) || 0
          const b = this.toNum(denArr[i]) || 0
          out.push(b ? Number((a / b).toFixed(decimals)) : 0)
        }
        return out
      }
      return {
        xData: numerator.xData.slice(),
        current: mapPeriod('current'),
        yoy: mapPeriod('yoy'),
        mom: mapPeriod('mom'),
        yoyX: Array.isArray(numerator.yoyX) ? numerator.yoyX.slice() : [],
        momX: Array.isArray(numerator.momX) ? numerator.momX.slice() : [],
      }
    },
    // 单价/单台产值：严格用当前「销售额」「销售数量/设备数」趋势序列相除，避免和卡片展示不一致
    getDerivedTrendSeries(card) {
      if (!card) return this.emptySeries()
      if (card.key === 'avgPrice') {
        const sales = this.metricCards.find((c) => c.key === 'sales')
        const qty = this.metricCards.find((c) => c.key === 'qty')
        return this.composeRatioSeries(
          (sales && sales.series) || this.emptySeries(),
          (qty && qty.series) || this.emptySeries(),
          2,
        )
      }
      if (card.key === 'avgOutput') {
        const sales = this.metricCards.find((c) => c.key === 'sales')
        const device = this.metricCards.find((c) => c.key === 'deviceCount')
        return this.composeRatioSeries(
          (sales && sales.series) || this.emptySeries(),
          (device && device.series) || this.emptySeries(),
          2,
        )
      }
      return card.series || this.emptySeries()
    },
    // 商品趋势字段：文档示例误写成 erp_company_num，优先 goods_num
    extractGoodsTrend(trendData) {
      return this.extractTrendField(trendData, ['goods_num', 'num', 'sku_num', 'erp_company_num'])
    },
    buildTrendSeriesMap(saleTrend, goodsTrend, erpTrend, refundTrend) {
      // 销售额趋势拆解，再前端合成单价 / 单台产值
      const ordered = this.parseSaleTrendBundle(saleTrend)
      const money = ordered.money
      const qty = ordered.qty
      const device = ordered.device
      const store = this.extractTrendField(erpTrend, ['erp_company_num', 'company_num', 'store_num'])
      const sku = this.extractGoodsTrend(goodsTrend)
      const refund = this.extractTrendField(refundTrend, ['refund_num', 'num', 'sum_num'])

      // 平均销售单价 = 销售额 ÷ 销售数量（绝不能反）
      const avgPrice = this.composeRatioSeries(money, qty, 2)
      // 平均单台产值 = 销售额 ÷ 产生销售设备数量（绝不能反）
      const avgOutput = this.composeRatioSeries(money, device, 2)

      // 退款率 = 日退款量 / (日销量 + 日退款量)（前端合成）
      const baseX = (qty.xData && qty.xData.length) ? qty.xData : (refund.xData || [])
      const refundRate = {
        xData: baseX,
        current: [],
        yoy: [],
        mom: [],
        yoyX: (qty.yoyX && qty.yoyX.length) ? qty.yoyX.slice() : ((refund.yoyX && refund.yoyX.slice()) || []),
        momX: (qty.momX && qty.momX.length) ? qty.momX.slice() : ((refund.momX && refund.momX.slice()) || []),
      }
      ;['current', 'yoy', 'mom'].forEach((period) => {
        for (let i = 0; i < baseX.length; i++) {
          const r = (refund[period] && refund[period][i]) || 0
          const n = (qty[period] && qty[period][i]) || 0
          refundRate[period].push((!r && !n) ? 0 : Number(((r / (n + r)) * 100).toFixed(2)))
        }
      })

      const skuSeries = sku.xData.length ? sku : this.emptySeries()

      return {
        sales: money,
        qty,
        avgPrice,
        refundRate,
        storeCount: store,
        deviceCount: device,
        skuCount: skuSeries,
        avgOutput,
      }
    },
    buildCardsFromMetrics(bundle, seriesMap) {
      return METRIC_DEFS.map((def) => {
        const item = (bundle && bundle[def.key]) || {}
        return {
          ...def,
          value: item.value != null ? item.value : null,
          yoy: item.yoy != null ? item.yoy : null,
          mom: item.mom != null ? item.mom : null,
          series: (seriesMap && seriesMap[def.key]) || this.emptySeries(),
          available: item.value != null,
        }
      })
    },
    rebuildSeriesFromCache() {
      let seriesMap = {}
      try {
        seriesMap = this.buildTrendSeriesMap(
          this.trendCache.sale,
          this.trendCache.goods,
          this.trendCache.erp,
          this.trendCache.refund,
        )
      } catch (e) {
        console.error('总览2.0 趋势解析失败', e)
        seriesMap = {}
      }
      this.metricCards = this.buildCardsFromMetrics(this.metricBundle || {}, seriesMap)
      // 单价/单台产值强制按「销售额序列 ÷ 数量/设备序列」再算一遍，与趋势图展示同源
      this.metricCards = this.metricCards.map((card) => {
        if (card.key !== 'avgPrice' && card.key !== 'avgOutput') return card
        return { ...card, series: this.getDerivedTrendSeries(card) }
      })
    },
    // 各指标依赖的趋势接口
    getTrendNeeds(metricKey) {
      const needSale = ['sales', 'qty', 'avgPrice', 'deviceCount', 'avgOutput', 'refundRate'].includes(metricKey)
      return {
        sale: needSale,
        goods: metricKey === 'skuCount',
        erp: metricKey === 'storeCount',
        refund: metricKey === 'refundRate',
      }
    },
    async selectMetric(key) {
      if (!key) return
      this.activeMetric = key
      await this.ensureTrendForMetric(key)
      this.updateChartTip()
      this.$nextTick(() => {
        try {
          this.renderChart()
        } catch (e) {
          console.error('总览2.0 图表渲染失败', e)
        }
      })
    },
    async ensureTrendForMetric(key) {
      const needs = this.getTrendNeeds(key)
      const params = this.buildQueryParams()
      if (!params.start || !params.end) return

      const tasks = []
      if (needs.sale && !this.trendCache.sale) {
        tasks.push({ name: 'sale', req: getNewSaleAmountTrend(params), label: '销售趋势' })
      }
      if (needs.goods && !this.trendCache.goods) {
        tasks.push({ name: 'goods', req: getNewGoodsSumTrend(params), label: '商品SKU趋势' })
      }
      if (needs.erp && !this.trendCache.erp) {
        tasks.push({ name: 'erp', req: getNewErpCompanyTrend(params), label: '销售景区趋势' })
      }
      if (needs.refund && !this.trendCache.refund) {
        tasks.push({ name: 'refund', req: getNewRefundTrend(params), label: '退款趋势' })
      }

      if (!tasks.length) {
        this.rebuildSeriesFromCache()
        return
      }

      this.trendLoading = true
      this.chartTip = '趋势加载中…'
      try {
        const results = await Promise.allSettled(tasks.map((t) => t.req))
        const failMsgs = []
        results.forEach((res, i) => {
          const task = tasks[i]
          this.trendCache[task.name] = this.unwrapSettled(res)
          const err = this.getSettledError(res)
          if (err) failMsgs.push(`${task.label}：${err}`)
          else if (res.status === 'rejected') failMsgs.push(`${task.label}：请求失败`)
        })
        this.rebuildSeriesFromCache()
        if (failMsgs.length) {
          this.$message.warning(failMsgs[0])
          this.chartTip = failMsgs[0]
        }
      } finally {
        this.trendLoading = false
      }
    },
    updateChartTip() {
      if (this.trendLoading) {
        this.chartTip = '趋势加载中…'
        return
      }
      const card = this.metricCards.find((c) => c.key === this.activeMetric)
      const hasTrend = card && card.series && card.series.current && card.series.current.length
      if (!hasTrend) {
        this.chartTip = '点击卡片或指标筛选加载该趋势'
        return
      }
      const tips = {
        sales: '销售额日趋势 · 本期(current) / 环比(previous) / 同比(last_year)',
        qty: '销售数量日趋势 · 本期 / 环比 / 同比',
        avgPrice: '平均销售单价 = 销售额趋势 ÷ 销售数量',
        refundRate: '退款率 = 日退款量 / (日销量 + 日退款量)',
        storeCount: '销售景区数量日趋势',
        deviceCount: '产生销售设备数量日趋势',
        skuCount: '产生销售商品SKU数量日趋势',
        avgOutput: '平均单台产值 = 销售额趋势 ÷ 产生销售设备数量',
      }
      this.chartTip = tips[this.activeMetric] || '指标日趋势'
    },
    async handleQuery() {
      this.region = 'global'
      const params = this.buildQueryParams()
      if (!params.start || !params.end) return

      this.querying = true
      this.activeMetric = 'sales'
      this.trendCache = { sale: null, goods: null, erp: null, refund: null }
      this.metricBundle = null
      this.sourceList = []
      this.regionList = []
      this.provinceList = []
      this.regionViewMode = 'region'
      this.provinceLoading = false
      this.companyTypeList = []
      this.companyTypeMomRankMap = {}
      this.companyNameList = []
      this.companyNameMomRankMap = {}
      this.deviceRankRaw = null
      this.deviceMomRankMap = {}
      this.metricCards = METRIC_DEFS.map((def) => ({
        ...def,
        value: null,
        yoy: null,
        mom: null,
        series: { ...EMPTY_SERIES },
        available: false,
      }))
      this.dataTip = '加载中…'
      this.chartTip = '趋势加载中…'
      this.sourceTip = '加载中…'
      this.companyTypeTip = '加载中…'
      this.companyNameTip = '加载中…'
      this.deviceRankTip = '加载中…'
      this.moduleLoading = {
        summary: true,
        trend: true,
        source: true,
        region: true,
        companyType: false,
        companyName: true,
        device: true,
      }

      // 各模块并行请求，谁先完成谁先出数/关 loading
      await Promise.allSettled([
        this.loadSummaryModule(params),
        this.loadTrendModule(params),
        this.loadSourceModule(params),
        this.loadRegionModule(params),
        // 景区类型排名暂时隐藏，不请求
        // this.loadCompanyTypeModule(params),
        this.loadCompanyNameModule(params),
        this.loadDeviceModule(params),
      ])
      this.querying = false
    },
    async loadSummaryModule(params) {
      this.moduleLoading.summary = true
      try {
        const [saleSumRes, goodsSumRes, erpSumRes, refundSumRes] = await Promise.allSettled([
          getNewSaleSum(params),
          getNewGoodsSum(params),
          getNewErpCompanySum(params),
          getNewRefundSum(params),
        ])
        const saleSum = this.unwrapSettled(saleSumRes)
        const goodsSum = this.unwrapSettled(goodsSumRes)
        const erpSum = this.unwrapSettled(erpSumRes)
        const refundSum = this.unwrapSettled(refundSumRes)
        const failMsgs = this.collectPartialErrors([
          { name: '销售汇总', res: saleSumRes },
          { name: '商品汇总', res: goodsSumRes },
          { name: '销售景区汇总', res: erpSumRes },
          { name: '退款汇总', res: refundSumRes },
        ])
        const hasAnySum = !!(saleSum || goodsSum || erpSum || refundSum)
        if (!hasAnySum) {
          this.dataTip = failMsgs[0] || '汇总接口均无返回数据'
          this.metricBundle = null
          this.metricCards = METRIC_DEFS.map((def) => ({
            ...def,
            value: null,
            yoy: null,
            mom: null,
            series: { ...EMPTY_SERIES },
            available: false,
          }))
          return
        }
        this.metricBundle = this.buildMetricBundle(saleSum, goodsSum, erpSum, refundSum)
        this.rebuildSeriesFromCache()
        this.dataTip = failMsgs.length
          ? `部分接口异常：${failMsgs[0]}`
          : '已加载概况 · 其他趋势点击后再加载'
        this.$nextTick(() => {
          try {
            this.renderChart()
          } catch (e) {
            console.error('总览2.0 概况后趋势刷新失败', e)
          }
        })
      } catch (e) {
        console.error('总览2.0 概况加载失败', e)
        this.dataTip = '概况数据加载失败'
      } finally {
        this.moduleLoading.summary = false
      }
    },
    async loadTrendModule(params) {
      this.moduleLoading.trend = true
      this.chartTip = '趋势加载中…'
      try {
        const [saleTrendRes] = await Promise.allSettled([getNewSaleAmountTrend(params)])
        const saleTrend = this.unwrapSettled(saleTrendRes)
        this.trendCache.sale = saleTrend
        this.rebuildSeriesFromCache()
        const err = this.getSettledError(saleTrendRes)
        if (err) {
          this.chartTip = err
        } else {
          this.updateChartTip()
        }
        this.$nextTick(() => {
          try {
            this.renderChart()
          } catch (chartErr) {
            console.error('总览2.0 趋势图渲染失败', chartErr)
          }
        })
      } catch (e) {
        console.error('总览2.0 趋势加载失败', e)
        this.chartTip = '趋势数据加载失败'
      } finally {
        this.moduleLoading.trend = false
      }
    },
    async loadSourceModule(params) {
      this.moduleLoading.source = true
      try {
        const [sourceRes] = await Promise.allSettled([getNewSourceTrend(params)])
        this.applySourceList(this.unwrapSettled(sourceRes))
        this.sourceTip = this.sourcePieData.length
          ? `共 ${this.sourcePieData.length} 类来源`
          : (this.getSettledError(sourceRes) || '暂无来源销售数据')
        this.$nextTick(() => this.renderSourceChart())
      } catch (e) {
        console.error('总览2.0 来源加载失败', e)
        this.sourceList = []
        this.sourceTip = '来源数据加载失败'
        this.$nextTick(() => this.renderSourceChart())
      } finally {
        this.moduleLoading.source = false
      }
    },
    async loadRegionModule(params) {
      this.moduleLoading.region = true
      try {
        const [regionRes] = await Promise.allSettled([getNewRegionTrend(params)])
        this.applyRegionList(this.unwrapSettled(regionRes))
        this.regionViewMode = 'region'
      } catch (e) {
        console.error('总览2.0 地区加载失败', e)
        this.regionList = []
      } finally {
        this.moduleLoading.region = false
      }
    },
    async loadCompanyTypeModule(params) {
      this.moduleLoading.companyType = true
      try {
        const momParams = this.buildMomQueryParams(params)
        const [companyTypeRes, companyTypeMomRes] = await Promise.allSettled([
          getNewCompanyTypeTrend(params),
          momParams ? getNewCompanyTypeTrend(momParams) : Promise.resolve(null),
        ])
        this.applyCompanyTypeList(this.unwrapSettled(companyTypeRes))
        this.companyTypeMomRankMap = momParams
          ? this.buildCompanyTypeMomRankMap(this.unwrapSettled(companyTypeMomRes))
          : {}
        this.companyTypeTip = this.companyTypeRankList.length
          ? `共 ${this.companyTypeRankList.length} 类 · 含环比名次`
          : (this.getSettledError(companyTypeRes) || '暂无景区类型数据')
      } catch (e) {
        console.error('总览2.0 景区类型加载失败', e)
        this.companyTypeList = []
        this.companyTypeMomRankMap = {}
        this.companyTypeTip = '景区类型加载失败'
      } finally {
        this.moduleLoading.companyType = false
      }
    },
    async loadCompanyNameModule(params) {
      this.moduleLoading.companyName = true
      try {
        const momParams = this.buildMomQueryParams(params)
        const [companyNameRes, companyNameMomRes] = await Promise.allSettled([
          getNewCompanyName(params),
          momParams ? getNewCompanyName(momParams) : Promise.resolve(null),
        ])
        this.applyCompanyNameList(this.unwrapSettled(companyNameRes))
        this.companyNameMomRankMap = momParams
          ? this.buildCompanyMomRankMap(this.unwrapSettled(companyNameMomRes))
          : {}
        this.companyNameTip = this.companyNameRankList.length
          ? `共 ${this.companyNameRankList.length} 家 · 含环比名次`
          : (this.getSettledError(companyNameRes) || '暂无景区销售数据')
      } catch (e) {
        console.error('总览2.0 景区销售加载失败', e)
        this.companyNameList = []
        this.companyNameMomRankMap = {}
        this.companyNameTip = '景区销售加载失败'
      } finally {
        this.moduleLoading.companyName = false
      }
    },
    async loadDeviceModule(params) {
      this.moduleLoading.device = true
      try {
        const momParams = this.buildMomQueryParams(params)
        const [deviceSortRes, deviceSortMomRes] = await Promise.allSettled([
          getBIOne(params),
          momParams ? getBIOne(momParams) : Promise.resolve(null),
        ])
        this.applyDeviceRank(this.unwrapSettled(deviceSortRes))
        this.deviceMomRankMap = momParams
          ? this.buildDeviceMomRankMap(this.unwrapSettled(deviceSortMomRes))
          : {}
        this.deviceRankTip = this.deviceRankData
          ? `共 ${this.deviceRankData.yData.length} 台 · 含环比名次`
          : (this.getSettledError(deviceSortRes) || '暂无设备销售数据')
      } catch (e) {
        console.error('总览2.0 设备排名加载失败', e)
        this.deviceRankRaw = null
        this.deviceMomRankMap = {}
        this.deviceRankTip = '设备销售加载失败'
      } finally {
        this.moduleLoading.device = false
      }
    },
    getSourceLabel(source) {
      const key = this.normalizeSourceCode(source)
      // 优先库内 source 码，再筛选 value，再设备筛选项
      if (SOURCE_DB_LABELS[key]) return SOURCE_DB_LABELS[key]
      if (SOURCE_DISPLAY_NAMES[key]) return SOURCE_DISPLAY_NAMES[key]
      const found = DEVICE_OPTIONS.find((d) => d.value === key)
      return found ? found.label : (key ? `来源${key}` : '未知来源')
    },
    getFilterDeviceLabel(filterVal) {
      const key = String(filterVal == null ? '' : filterVal)
      if (SOURCE_DISPLAY_NAMES[key]) return SOURCE_DISPLAY_NAMES[key]
      const found = DEVICE_OPTIONS.find((d) => d.value === key)
      return found ? found.label : (key ? `设备${key}` : '未知设备')
    },
    // 兼容 "2" / "2.0" / 2
    normalizeSourceCode(raw) {
      if (raw == null || raw === '') return ''
      const n = Number(raw)
      if (!Number.isNaN(n) && Number.isFinite(n)) return String(Math.trunc(n))
      const text = String(raw).trim()
      const m = text.match(/^(\d+)/)
      return m ? m[1] : text
    },
    formatInt(val) {
      const n = Number(val)
      if (Number.isNaN(n)) return '--'
      return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    formatMoney(val) {
      const n = Number(val)
      if (Number.isNaN(n)) return '--'
      return `¥${n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
    },
    normalizeList(data) {
      if (Array.isArray(data)) return data
      return []
    },
    // 兼容对象 {source,sale_num,sale_money} 与元组 [source, sale_num, sale_money]
    parseSourceRow(item) {
      if (item == null) return null
      if (Array.isArray(item)) {
        return {
          source: this.normalizeSourceCode(item[0]),
          sale_num: Number(item[1]) || 0,
          sale_money: Number(item[2]) || 0,
        }
      }
      if (typeof item === 'object') {
        return {
          source: this.normalizeSourceCode(item.source),
          sale_num: Number(item.sale_num) || 0,
          sale_money: Number(item.sale_money) || 0,
        }
      }
      return null
    },
    // 兼容对象与元组：region_id / erp_company_region + sale_num + sale_money
    parseRegionRow(item) {
      if (item == null) return null
      if (Array.isArray(item)) {
        const idOrName = item[0] != null ? String(item[0]) : ''
        const looksLikeId = idOrName !== '' && !Number.isNaN(Number(idOrName))
        return {
          region_id: looksLikeId ? String(Number(idOrName)) : '',
          erp_company_region: looksLikeId ? '' : idOrName,
          sale_num: Number(item[1]) || 0,
          sale_money: Number(item[2]) || 0,
        }
      }
      if (typeof item === 'object') {
        const rawId = item.region_id != null
          ? item.region_id
          : (item.erp_company_region_id != null ? item.erp_company_region_id : item.region)
        const regionId = rawId != null && rawId !== '' ? String(rawId) : ''
        const nameHint = item.erp_company_region != null
          ? String(item.erp_company_region)
          : (item.region_name != null ? String(item.region_name) : '')
        // 若 nameHint 本身是纯数字，当作 region_id
        const nameIsId = nameHint !== '' && !Number.isNaN(Number(nameHint)) && String(Number(nameHint)) === nameHint
        return {
          region_id: regionId || (nameIsId ? nameHint : ''),
          erp_company_region: nameIsId ? '' : nameHint,
          sale_num: Number(item.sale_num) || 0,
          sale_money: Number(item.sale_money) || 0,
        }
      }
      return null
    },
    resolveRegionName(item) {
      if (!item) return '未知地区'
      const id = item.region_id != null && item.region_id !== '' ? String(item.region_id) : ''
      if (id && this.regionNameMap[id]) return this.regionNameMap[id]
      if (item.erp_company_region) {
        const tip = String(item.erp_company_region)
        // 已是名称则直接用；若仍是 id 再查一遍字典
        if (this.regionNameMap[tip]) return this.regionNameMap[tip]
        if (Number.isNaN(Number(tip))) return tip
      }
      if (id) return `地区${id}`
      return '未知地区'
    },
    // 统一成 6 位省级码：460000 / 46 / 460100 → 460000
    normalizeProvinceCode(raw) {
      if (raw == null || raw === '') return ''
      let text = raw
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        text = String(Math.trunc(raw))
      } else {
        text = String(raw).trim()
      }
      const digits = String(text).replace(/\D/g, '')
      if (!digits) return ''
      const two = digits.slice(0, 2)
      if (!two) return ''
      return `${two}0000`
    },
    // 编码或中文名 → 省级 6 位码
    resolveProvinceCode(item) {
      const raw = item && item.erp_company_province != null ? item.erp_company_province : ''
      if (raw === '' || raw == null) return ''
      const code = this.normalizeProvinceCode(raw)
      if (code && PROVINCE_NAME_MAP[code]) return code
      const text = String(raw).trim()
      if (PROVINCE_NAME_MAP[text]) return text.length === 6 ? text : this.normalizeProvinceCode(text)
      const hit = Object.keys(PROVINCE_NAME_MAP).find((k) => k.length === 6 && PROVINCE_NAME_MAP[k] === text)
      return hit || code || ''
    },
    // erp_company_province 可能是 "440000" / 440000 / "46" / 中文名
    resolveProvinceName(item) {
      const code = this.resolveProvinceCode(item)
      if (code && PROVINCE_NAME_MAP[code]) return PROVINCE_NAME_MAP[code]
      const raw = item && item.erp_company_province != null ? String(item.erp_company_province).trim() : ''
      if (!raw) return '未知省份'
      if (PROVINCE_NAME_MAP[raw]) return PROVINCE_NAME_MAP[raw]
      if (raw && !/\d/.test(raw)) return raw
      return code ? `省份${code}` : raw
    },
    applySourceList(data) {
      this.sourceList = this.normalizeList(data)
        .map((item) => this.parseSourceRow(item))
        .filter((item) => item && (item.source !== '' || item.sale_money > 0 || item.sale_num > 0))
        .sort((a, b) => b.sale_money - a.sale_money)
    },
    applyRegionList(data) {
      this.regionList = this.normalizeList(data)
        .map((item) => this.parseRegionRow(item))
        .filter((item) => item && (item.region_id || item.erp_company_region || item.sale_money > 0))
    },
    isMainlandChinaRegion(item) {
      if (!item) return false
      const id = item.region_id != null ? String(item.region_id) : ''
      if (id === '1') return true
      const name = item.name || item.erp_company_region || ''
      return String(name).includes('中国大陆') || String(name) === '大陆'
    },
    parseProvinceRow(item) {
      if (item == null) return null
      if (Array.isArray(item)) {
        return {
          erp_company_province: item[0] != null ? String(item[0]) : '',
          sale_num: Number(item[1]) || 0,
          sale_money: Number(item[2]) || 0,
        }
      }
      if (typeof item === 'object') {
        return {
          erp_company_province: item.erp_company_province != null
            ? String(item.erp_company_province)
            : (item.province != null ? String(item.province) : ''),
          sale_num: Number(item.sale_num) || 0,
          sale_money: Number(item.sale_money) || 0,
        }
      }
      return null
    },
    applyProvinceList(data) {
      this.provinceList = this.normalizeList(data)
        .map((item) => this.parseProvinceRow(item))
        .filter((item) => item && (item.erp_company_province || item.sale_money > 0))
    },
    async openProvinceRank() {
      if (this.provinceLoading) return
      const params = this.buildQueryParams()
      if (!params.start || !params.end) return
      this.provinceLoading = true
      this.regionViewMode = 'province'
      try {
        const res = await Promise.allSettled([getNewCompanyProvinceTrend(params)])
        const data = this.unwrapSettled(res[0])
        this.applyProvinceList(data)
        if (!this.provinceRankList.length) {
          const err = this.getSettledError(res[0])
          if (err) this.$message.warning(err)
        }
      } catch (e) {
        console.error('省份销售加载失败', e)
        this.provinceList = []
        this.$message.error('省份销售数据加载失败')
      } finally {
        this.provinceLoading = false
      }
    },
    backToRegionRank() {
      this.regionViewMode = 'region'
    },
    onRegionRankClick(item) {
      if (this.regionViewMode === 'province') return
      if (item && item.canDrill) this.openProvinceRank()
    },
    // 兼容对象与元组 [type, sale_num, sale_money]
    parseCompanyTypeRow(item) {
      if (item == null) return null
      if (Array.isArray(item)) {
        return {
          erp_company_type: item[0] != null ? String(item[0]) : '',
          sale_num: Number(item[1]) || 0,
          sale_money: Number(item[2]) || 0,
        }
      }
      if (typeof item === 'object') {
        return {
          erp_company_type: item.erp_company_type != null ? String(item.erp_company_type) : '',
          sale_num: Number(item.sale_num) || 0,
          sale_money: Number(item.sale_money) || 0,
        }
      }
      return null
    },
    // 兼容对象与元组 [id, name, sale_num, sale_money] / [name, sale_num, sale_money]
    parseCompanyNameRow(item) {
      if (item == null) return null
      if (Array.isArray(item)) {
        if (item.length >= 4) {
          return {
            erp_company_id: item[0] != null ? String(item[0]) : '',
            erp_company_name: item[1] != null ? String(item[1]) : '',
            sale_num: Number(item[2]) || 0,
            sale_money: Number(item[3]) || 0,
          }
        }
        return {
          erp_company_id: '',
          erp_company_name: item[0] != null ? String(item[0]) : '',
          sale_num: Number(item[1]) || 0,
          sale_money: Number(item[2]) || 0,
        }
      }
      if (typeof item === 'object') {
        return {
          erp_company_id: item.erp_company_id != null ? String(item.erp_company_id) : '',
          erp_company_name: item.erp_company_name != null ? String(item.erp_company_name) : '',
          sale_num: Number(item.sale_num) || 0,
          sale_money: Number(item.sale_money) || 0,
        }
      }
      return null
    },
    applyCompanyTypeList(data) {
      this.companyTypeList = this.normalizeList(data)
        .map((item) => this.parseCompanyTypeRow(item))
        .filter((item) => item && (item.erp_company_type || item.sale_money > 0))
    },
    applyCompanyNameList(data) {
      this.companyNameList = this.normalizeList(data)
        .map((item) => this.parseCompanyNameRow(item))
        .filter((item) => item && (item.erp_company_name || item.sale_money > 0))
    },
    // 总览 device_sort：{ xData: 金额[], yData: 设备名[] }
    applyDeviceRank(data) {
      const sort = (data && data.device_sort) || data
      if (!sort || !Array.isArray(sort.xData) || !Array.isArray(sort.yData)) {
        this.deviceRankRaw = null
        return
      }
      this.deviceRankRaw = {
        xData: sort.xData.slice(),
        yData: sort.yData.slice(),
      }
    },
    initSourceChart() {
      if (!this.$refs.sourceChart) return
      if (!this.sourceChart) this.sourceChart = echarts.init(this.$refs.sourceChart)
      this.renderSourceChart()
    },
    renderSourceChart() {
      if (!this.sourceChart) {
        if (this.$refs.sourceChart) this.sourceChart = echarts.init(this.$refs.sourceChart)
        else return
      }
      const list = this.sourceChartSlices
      if (!list.length) {
        this.sourceChart.clear()
        return
      }
      // 饼图扇区：0 销售额不画扇区；右侧列表展示销售额/占比
      const pieData = list.map((item) => ({
        name: item.name,
        value: Number(item.value) || 0,
        sale_num: item.sale_num,
        percentText: Number(item.percent || 0).toFixed(2),
        itemStyle: {
          color: item.color,
          opacity: Number(item.value) > 0 ? 1 : 0.4,
        },
      }))
      this.sourceChart.setOption({
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(10, 14, 39, 0.92)',
          borderColor: 'rgba(88, 217, 249, 0.45)',
          textStyle: { color: '#ffffff' },
          formatter: (p) => {
            const pct = (p.data && p.data.percentText) || p.percent
            return `${p.name}<br/>占比：${pct}%<br/>销售额：${this.formatMoney(p.value)}`
          },
        },
        legend: { show: false },
        series: [
          {
            name: '设备类型销售额',
            type: 'pie',
            radius: ['42%', '68%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: true,
            minAngle: SOURCE_PIE_MIN_ANGLE,
            stillShowZeroSum: false,
            itemStyle: {
              borderRadius: 5,
              borderColor: 'rgba(10, 14, 39, 0.92)',
              borderWidth: 2,
            },
            label: { show: false },
            labelLine: { show: false },
            emphasis: {
              scale: true,
              scaleSize: 8,
              itemStyle: {
                shadowBlur: 16,
                shadowColor: 'rgba(88, 217, 249, 0.35)',
              },
              label: {
                show: true,
                position: 'outside',
                formatter: (p) => {
                  const pct = (p.data && p.data.percentText) || p.percent
                  return `${p.name}\n${pct}%`
                },
                color: '#ffffff',
                fontSize: 12,
                fontWeight: 600,
              },
              labelLine: {
                show: true,
                length: 12,
                length2: 10,
                lineStyle: { color: 'rgba(255,255,255,0.7)', width: 1.5 },
              },
            },
            data: pieData,
          },
        ],
      }, true)
      this.$nextTick(() => {
        if (this.sourceChart) this.sourceChart.resize()
      })
    },
    initChart() {
      if (!this.$refs.trendChart) return
      this.chart = echarts.init(this.$refs.trendChart)
      this.renderChart()
    },
    formatAxisValue(val, card, isMoney, isPercent) {
      if (isPercent) return `${Number(val).toFixed(2)}%`
      if (isMoney) {
        if (val >= 1000) return `¥${(val / 1000).toFixed(1)}k`
        return `¥${Number(val).toFixed(0)}`
      }
      return String(Math.round(val))
    },
    formatPointValue(raw, card, isMoney, isPercent) {
      if (isPercent) return `${raw.toFixed(2)}%`
      if (isMoney) return `¥${raw.toFixed(2)}`
      return raw.toFixed(card.decimals)
    },
    buildNeonLine({
      name,
      data,
      color,
      softColor,
      showArea = false,
      showSymbol = false,
      dashed = false,
    }) {
      const series = []
      if (!data || !data.length) return series
      // 拷贝，避免 glow/主线或同比/环比共用同一数组引用
      const lineData = data.slice()

      series.push({
        name: `${name}-glow`,
        type: 'line',
        smooth: true,
        symbol: 'none',
        silent: true,
        tooltip: { show: false },
        legendHoverLink: false,
        z: 1,
        lineStyle: {
          width: dashed ? 6 : 10,
          color: softColor,
          shadowBlur: 28,
          shadowColor: color,
          type: dashed ? 'dashed' : 'solid',
        },
        data: lineData,
      })
      const main = {
        name,
        type: 'line',
        smooth: true,
        showSymbol,
        symbol: 'circle',
        symbolSize: 10,
        z: 3,
        lineStyle: {
          width: 3,
          color,
          shadowBlur: 14,
          shadowColor: color,
          type: dashed ? 'dashed' : 'solid',
          cap: 'round',
        },
        itemStyle: {
          color,
          borderColor: '#0a0e27',
          borderWidth: 2,
          shadowBlur: 16,
          shadowColor: color,
        },
        emphasis: {
          focus: 'series',
          scale: true,
          itemStyle: {
            color: '#fff',
            borderColor: color,
            borderWidth: 3,
            shadowBlur: 24,
            shadowColor: color,
          },
        },
        data: lineData.slice(),
      }
      if (showArea) {
        main.areaStyle = {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: color === '#00d4ff' ? 'rgba(0, 212, 255, 0.45)' : softColor },
            { offset: 0.5, color: color === '#00d4ff' ? 'rgba(0, 212, 255, 0.14)' : 'rgba(0,0,0,0)' },
            { offset: 1, color: 'rgba(0, 212, 255, 0.01)' },
          ]),
          shadowColor: color,
          shadowBlur: 20,
        }
      }
      if (showSymbol && data.length) {
        const max = Math.max(...data)
        const min = Math.min(...data)
        const maxIdx = data.indexOf(max)
        const minIdx = data.indexOf(min)
        main.markPoint = {
          symbol: 'pin',
          symbolSize: 42,
          label: {
            color: '#fff',
            fontSize: 10,
            fontWeight: 'bold',
          },
          data: [
            {
              name: '峰值',
              coord: [maxIdx, max],
              itemStyle: {
                color,
                shadowBlur: 18,
                shadowColor: color,
              },
              label: { formatter: '峰值' },
            },
            {
              name: '低谷',
              coord: [minIdx, min],
              itemStyle: {
                color: '#8b5cf6',
                shadowBlur: 18,
                shadowColor: '#8b5cf6',
              },
              label: { formatter: '低谷' },
            },
          ],
        }
      }
      series.push(main)
      return series
    },
    renderChart() {
      if (!this.chart) {
        this.initChart()
      }
      if (!this.chart) return

      const card = this.metricCards.find((c) => c.key === this.activeMetric) || this.metricCards[0]
      if (!card) return

      const trendSeries = this.getDerivedTrendSeries(card)
      const isMoney = ['sales', 'avgPrice', 'avgOutput'].includes(card.key)
      const isPercent = card.key === 'refundRate'
      const xData = (trendSeries && trendSeries.xData && trendSeries.xData.length)
        ? trendSeries.xData
        : []
      const hasCurrent = trendSeries && trendSeries.current && trendSeries.current.length
      const hasYoy = trendSeries && trendSeries.yoy && trendSeries.yoy.length
      const hasMom = trendSeries && trendSeries.mom && trendSeries.mom.length

      // 同比/环比日序列长度可能与当前不一致，按索引对齐
      const alignSeries = (arr) => {
        if (!arr || !arr.length || !xData.length) return arr || []
        if (arr.length === xData.length) return arr
        if (arr.length > xData.length) return arr.slice(0, xData.length)
        return arr
      }

      const dense = xData.length > 40
      const currentData = hasCurrent ? trendSeries.current.slice() : []
      const yoyData = hasYoy ? alignSeries(trendSeries.yoy).slice() : []
      const momData = hasMom ? alignSeries(trendSeries.mom).slice() : []
      const yoyX = hasYoy ? alignSeries(trendSeries.yoyX || []).slice() : []
      const momX = hasMom ? alignSeries(trendSeries.momX || []).slice() : []

      // 点太多时关掉圆点/峰值标记，避免糊成一片
      const currentSeries = this.buildNeonLine({
        name: '查询数据',
        data: currentData,
        color: '#00d4ff',
        softColor: 'rgba(0, 212, 255, 0.28)',
        showArea: true,
        showSymbol: !dense,
      })
      const yoySeries = this.buildNeonLine({
        name: '同比数据',
        data: yoyData,
        color: '#ec4899',
        softColor: 'rgba(236, 72, 153, 0.22)',
        dashed: true,
        showSymbol: false,
      })
      const momSeries = this.buildNeonLine({
        name: '环比数据',
        data: momData,
        color: '#22c55e',
        softColor: 'rgba(34, 197, 94, 0.22)',
        showSymbol: false,
      })

      const legendData = ['查询数据']
      if (hasYoy) legendData.push('同比数据')
      if (hasMom) legendData.push('环比数据')

      const option = {
        backgroundColor: 'transparent',
        animation: true,
        animationDuration: dense ? 600 : 1200,
        animationEasing: 'cubicOut',
        animationDurationUpdate: 700,
        color: ['#00d4ff', '#ec4899', '#22c55e'],
        title: !hasCurrent
          ? {
              text: '暂无趋势数据',
              left: 'center',
              top: 'middle',
              textStyle: {
                color: 'rgba(229, 244, 255, 0.45)',
                fontSize: 16,
                fontWeight: 600,
              },
            }
          : { show: false },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(10, 14, 39, 0.92)',
          borderColor: 'rgba(0, 212, 255, 0.65)',
          borderWidth: 1,
          padding: [12, 14],
          extraCssText:
            'box-shadow: 0 0 24px rgba(0, 212, 255, 0.35), inset 0 0 20px rgba(0, 212, 255, 0.08); backdrop-filter: blur(8px); border-radius: 10px;',
          textStyle: { color: '#e5f4ff', fontSize: 12 },
          axisPointer: {
            type: 'cross',
            crossStyle: { color: 'rgba(0, 212, 255, 0.55)', width: 1 },
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.55)',
              width: 1,
              type: 'dashed',
              shadowBlur: 8,
              shadowColor: '#00d4ff',
            },
            label: {
              backgroundColor: 'rgba(0, 212, 255, 0.85)',
              color: '#0a0e27',
              borderRadius: 4,
              fontWeight: 'bold',
            },
          },
          formatter: (params) => {
            if (!params || !params.length) return ''
            const visible = params.filter((p) => !String(p.seriesName).endsWith('-glow'))
            if (!visible.length) return ''
            const lines = visible.map((p, i) => {
              const raw = Number(p.value) || 0
              const text = this.formatPointValue(raw, card, isMoney, isPercent)
              let dateText = ''
              if (p.seriesName === '查询数据') {
                dateText = xData[p.dataIndex] != null
                  ? String(xData[p.dataIndex])
                  : String(p.axisValue || '')
              } else if (p.seriesName === '同比数据') {
                dateText = yoyX[p.dataIndex] != null ? String(yoyX[p.dataIndex]) : ''
              } else if (p.seriesName === '环比数据') {
                dateText = momX[p.dataIndex] != null ? String(momX[p.dataIndex]) : ''
              }
              const color = p.color || '#00d4ff'
              return `<div style="display:flex;flex-direction:column;${i ? 'margin-top:10px;padding-top:8px;border-top:1px solid rgba(0,212,255,0.18);' : ''}">
                <div style="display:flex;align-items:center;justify-content:space-between;gap:18px;">
                  <span>${p.marker}<span style="color:#e5f4ff">${p.seriesName}</span></span>
                  <span style="color:${color};font-weight:700;font-variant-numeric:tabular-nums">${dateText}</span>
                </div>
                <div style="margin-top:4px;padding-left:16px;color:#00d4ff;font-weight:700;font-size:13px;font-variant-numeric:tabular-nums">${text}</div>
              </div>`
            })
            return lines.join('')
          },
        },
        legend: {
          top: 10,
          right: 24,
          icon: 'roundRect',
          itemWidth: 14,
          itemHeight: 8,
          itemGap: 18,
          data: legendData,
          textStyle: {
            color: '#e5f4ff',
            fontSize: 12,
            fontWeight: 600,
          },
        },
        grid: {
          left: 52,
          right: 36,
          top: 58,
          bottom: dense ? 72 : 40,
        },
        dataZoom: dense
          ? [
              {
                type: 'slider',
                height: 18,
                bottom: 12,
                start: 0,
                end: Math.min(100, Math.max(12, (60 / xData.length) * 100)),
                borderColor: 'rgba(0, 212, 255, 0.35)',
                backgroundColor: 'rgba(0, 212, 255, 0.06)',
                fillerColor: 'rgba(0, 212, 255, 0.18)',
                handleStyle: { color: '#00d4ff' },
                textStyle: { color: 'rgba(229, 244, 255, 0.65)', fontSize: 10 },
                dataBackground: {
                  lineStyle: { color: 'rgba(0, 212, 255, 0.35)' },
                  areaStyle: { color: 'rgba(0, 212, 255, 0.08)' },
                },
              },
            ]
          : undefined,
        graphic: [
          {
            type: 'group',
            left: 18,
            top: 18,
            children: [
              {
                type: 'text',
                style: {
                  text: card.trendLabel || card.name,
                  fill: 'rgba(0, 212, 255, 0.9)',
                  font: 'bold 13px sans-serif',
                  shadowBlur: 10,
                  shadowColor: 'rgba(0, 212, 255, 0.8)',
                },
              },
            ],
          },
        ],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xData,
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.45)',
              width: 1,
              shadowBlur: 8,
              shadowColor: 'rgba(0, 212, 255, 0.4)',
            },
          },
          axisLabel: {
            color: 'rgba(229, 244, 255, 0.8)',
            fontWeight: 600,
            margin: 14,
            hideOverlap: true,
            rotate: dense ? 35 : 0,
            formatter: (val) => {
              // 日期过长时只展示 MM-DD
              if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
                return val.slice(5)
              }
              return val
            },
          },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.06)',
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'value',
          scale: true,
          axisLine: { show: false },
          splitLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.1)',
              type: 'dashed',
            },
          },
          axisLabel: {
            color: 'rgba(229, 244, 255, 0.55)',
            formatter: (val) => this.formatAxisValue(val, card, isMoney, isPercent),
          },
        },
        series: [...currentSeries, ...yoySeries, ...momSeries],
      }

      this.chart.setOption(option, true)

      this.chart.off('legendselectchanged')
      this.chart.on('legendselectchanged', (params) => {
        const selected = { ...params.selected }
        ;['查询数据', '同比数据', '环比数据'].forEach((name) => {
          if (Object.prototype.hasOwnProperty.call(selected, name)) {
            selected[`${name}-glow`] = selected[name]
          }
        })
        this.chart.setOption({ legend: { selected } })
      })
    },
  },
}
</script>
<style scoped lang="scss">
/* 与景区指标/设备状态等模块统一：紫边框 + 轻阴影，弱化霓虹青光 */
.overview-two {
  position: relative;
  margin: 30px auto 24px;
  width: 95%;
  padding: 20px;
  border-radius: 30px;
  color: #ffffff;
  overflow: visible;
  border: 1px solid #5c01b8;
  background: transparent;
  box-shadow: 0 0 10px;
  font-size: 16px;
  box-sizing: border-box;
}

.overview-two__bg {
  display: none;
}

.filter-panel,
.summary-section,
.chart-section,
.bottom-charts {
  position: relative;
  z-index: 1;
}

.filter-panel {
  padding: 8px 0 12px;
  border: none;
  background: transparent;
}

/* 对齐总览1.0 .control：整体椭圆框，适配多行条件 */
.filter-control {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 18px 24px 16px;
  border-radius: 28px;
  background: transparent;
  border: 1px solid rgba(88, 217, 249, 0.65);
  box-shadow:
    0 0 10px rgba(88, 217, 249, 0.35),
    0 0 22px rgba(92, 1, 184, 0.28),
    inset 0 0 12px rgba(88, 217, 249, 0.08);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;

  &--actions {
    padding-top: 2px;
    padding-left: calc(72px + 14px);
  }
}

.filter-label {
  flex: 0 0 72px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
}

.date-picker {
  width: 280px;
}

.query-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 34px;
  min-width: 88px;
  padding: 0 16px;
  border: 1px solid rgba(88, 217, 249, 0.85);
  border-radius: 18px;
  background: linear-gradient(135deg, #5c01b8 0%, #7c3aed 55%, #0284c7 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow:
    0 0 10px rgba(88, 217, 249, 0.4),
    0 3px 10px rgba(92, 1, 184, 0.4);
  transition: all 0.2s ease;

  i {
    font-size: 13px;
  }

  &:hover:not(:disabled) {
    color: #ffffff;
    border-color: #58d9f9;
    background: linear-gradient(135deg, #6d28d9 0%, #8b5cf6 50%, #0ea5e9 100%);
    transform: translateY(-1px);
    box-shadow:
      0 0 18px rgba(88, 217, 249, 0.65),
      0 6px 18px rgba(92, 1, 184, 0.5);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
}

.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pill {
  border: 1px solid rgba(92, 1, 184, 0.75);
  background: transparent;
  color: #ffffff;
  border-radius: 20px;
  padding: 7px 14px;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.22s ease;

  &:hover {
    transform: none;
    border-color: #5c01b8;
    background: rgba(92, 1, 184, 0.25);
    box-shadow: none;
  }

  &.active {
    color: #ffffff;
    background: #5c01b8;
    border-color: #5c01b8;
    box-shadow: none;
  }

  &.outline {
    background: transparent;
    border-color: rgba(92, 1, 184, 0.55);
    color: rgba(255, 255, 255, 0.9);

    &.active {
      color: #ffffff;
      background: rgba(92, 1, 184, 0.45);
      border-color: #5c01b8;
      box-shadow: none;
    }
  }
}

.summary-section,
.chart-section {
  margin-top: 22px;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.section-title {
  margin: 0;
  font-size: 22px;
  font-weight: bold;
  letter-spacing: 0;
  color: #ffffff;
  text-shadow: none;
}

.section-tip {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.metric-card {
  position: relative;
  padding: 16px 16px 14px;
  border-radius: 16px;
  background: transparent;
  border: 1px solid #5c01b8;
  box-shadow: 0 0 8px rgba(92, 1, 184, 0.35);
  cursor: pointer;
  transition: all 0.25s ease;
  overflow: hidden;

  &::after {
    display: none;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: #7a1fd4;
    box-shadow: 0 0 12px rgba(92, 1, 184, 0.55);
  }

  &.active {
    border-color: #58d9f9;
    box-shadow: 0 0 12px rgba(88, 217, 249, 0.35);
  }
}

.metric-card__top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.metric-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  box-shadow: none;
}

.metric-name {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
}

.metric-value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 0.3px;
  color: #ffffff;
  margin-bottom: 10px;
  font-variant-numeric: tabular-nums;
  text-shadow: none;
}

.metric-trends {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.trend {
  font-size: 12px;
  font-weight: 600;

  i {
    margin: 0 2px;
  }

  &.up {
    color: #ff6b9d;
  }

  &.down {
    color: #7cffb2;
  }

  &.na {
    color: rgba(255, 255, 255, 0.45);
  }
}

.chart-head {
  align-items: flex-start;
  gap: 12px;
}

.chart-head-left {
  flex: 1;
  min-width: 0;
}

.metric-pills {
  margin-top: 12px;
}

.chart-note {
  margin: 8px 0 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.chart-panel {
  position: relative;
  border-radius: 16px;
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 10px 10px 6px;
  overflow: hidden;

  &::before,
  &::after {
    display: none;
  }
}

.chart-panel-inner-corners,
.chart-scanline {
  display: none;
}

.trend-chart {
  width: 100%;
  height: 360px;
}

.bottom-charts {
  position: relative;
  z-index: 1;
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.company-charts {
  margin-top: 16px;
}

/* 底部四个卡片统一固定高度 */
.source-card,
.region-card,
.company-type-card,
.company-name-card,
.device-rank-card {
  height: 560px;
  max-height: 560px;
  min-height: 560px;
}

.company-rank-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(88, 217, 249, 0.55) rgba(255, 255, 255, 0.08);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.06);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(88, 217, 249, 0.45);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(88, 217, 249, 0.7);
  }
}

.device-charts {
  margin-top: 16px;
  justify-content: start;
}

.section-title-hint {
  margin-left: 4px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 224, 138, 0.92);
}

.bottom-card {
  box-sizing: border-box;
  padding: 20px;
  border-radius: 16px;
  background: transparent;
  border: 1px solid #5c01b8;
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.12);
  min-height: 460px;
  display: flex;
  flex-direction: column;
}

.bottom-card__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.region-head-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.region-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(88, 217, 249, 0.55);
  background: transparent;
  color: #58d9f9;
  font-size: 12px;
  cursor: pointer;
  line-height: 1.2;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(88, 217, 249, 0.12);
    border-color: rgba(88, 217, 249, 0.9);
  }
}

.source-charts {
  /* 与原先一致：半宽左对齐；内部再左右分栏 */
}

.source-layout {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  overflow: hidden;
}

.source-chart-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  align-self: stretch;
}

.source-chart {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.source-side-list {
  flex: 0 0 48%;
  max-width: none;
  min-width: 0;
  max-height: 100%;
  align-self: center;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 4px 6px 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(88, 217, 249, 0.55) rgba(255, 255, 255, 0.08);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.06);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(88, 217, 249, 0.45);
    border-radius: 4px;
  }
}

.source-side-item {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  border-bottom: 1px solid rgba(92, 1, 184, 0.28);
}

.source-side-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 8px rgba(88, 217, 249, 0.35);
}

.source-side-name {
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.source-side-money {
  color: #58d9f9;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
}

.source-side-pct {
  color: rgba(255, 224, 138, 0.95);
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
  min-width: 52px;
}

.chart-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
  pointer-events: none;
}

.company-rank-list {
  list-style: none;
  margin: 0;
  padding: 4px 0 8px;
}

.company-rank-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;

  &.is-drillable {
    cursor: pointer;
    border-radius: 8px;
    padding-left: 4px;
    padding-right: 4px;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(88, 217, 249, 0.08);
    }
  }
}

.company-rank-no {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  border: 1px solid rgba(88, 217, 249, 0.55);
  background: transparent;

  &.rank-1 {
    background: linear-gradient(135deg, #f6d365, #fda085);
    border-color: transparent;
    color: #3b1d0b;
  }

  &.rank-2 {
    background: linear-gradient(135deg, #d7dce5, #9aa4b2);
    border-color: transparent;
    color: #1f2937;
  }

  &.rank-3 {
    background: linear-gradient(135deg, #e0a86d, #b87333);
    border-color: transparent;
    color: #3b1d0b;
  }
}

.company-rank-main {
  flex: 1;
  min-width: 0;
}

.company-rank-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

.company-rank-name {
  font-size: 13px;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex: 1;
  min-width: 0;

  &.is-mainland {
    color: #ffe08a;
    font-weight: 700;
  }
}

.company-rank-money {
  margin-left: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #2bcbdf;
}

.rank-change {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  min-width: 72px;
  justify-content: flex-end;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  &.up {
    color: #f43f5e;
  }

  &.down {
    color: #22c55e;
  }

  &.flat {
    color: rgba(255, 255, 255, 0.45);
  }

  &.new {
    color: #fbbf24;
  }
}

.company-rank-track {
  height: 8px;
  border-radius: 999px;
  background: rgba(92, 1, 184, 0.25);
  overflow: hidden;
}

.company-rank-bar {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(92, 1, 184, 0.75), rgba(88, 217, 249, 0.95));
}

/* 地区默认青色；中国大陆金色；省份青色 */
.region-rank-bar {
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.25), rgba(88, 217, 249, 0.9));

  &.is-mainland-bar {
    background: linear-gradient(90deg, rgba(251, 191, 36, 0.35), rgba(253, 224, 71, 0.95));
  }

  &.is-province {
    background: linear-gradient(90deg, rgba(0, 212, 255, 0.25), rgba(88, 217, 249, 0.9));
  }
}

.chart-empty.static {
  position: relative;
  min-height: 240px;
}

@media (max-width: 1200px) {
  .bottom-charts {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .filter-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .chart-head {
    flex-direction: column;
  }

  .source-layout {
    flex-direction: column;
  }

  .source-side-list {
    flex: 1 1 auto;
    max-height: 200px;
  }

  .source-chart-wrap {
    min-height: 220px;
  }
}
</style>

<style lang="scss">
/* 与看板其它模块统一的日期控件 */
.overview-two {
  .el-range-editor.el-input__inner,
  .el-range-separator,
  .el-range-input {
    background: transparent !important;
    color: #ffffff !important;
  }

  .el-date-editor.el-input__inner {
    background: transparent !important;
    border: 1px solid #5c01b8 !important;
    border-radius: 20px;
  }

  .el-date-editor .el-range-input {
    color: #ffffff !important;
    background: transparent !important;
  }

  .el-range-separator {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  .el-input__icon {
    color: #ffffff !important;
  }
}
</style>
